<template>
  <div>
    <step-progress
      :steps="formSteps"
      :current-step="formStep"
      :passive-thickness="3"
      :active-thickness="6"
      :line-thickness="3"
      :icon-class="'icofont-check'"
      :active-color="'#fe415a'"
    ></step-progress>

    <form id="tour-form" @submit.prevent="tourClone" class="tour-form col pt-6 mb-4" autocomplete="off">
      <!-- form step 1 -->
      <div v-if="formStep === 0">
        <h4>All requested info and editorial instructions are required</h4>

        <!-- title input -->
        <h2 id="tour-title-sect" class="mt-6 text-primary">
          <strong>Tour title</strong>
          <span
            v-if="$v.form.title.$dirty && $v.form.title.$invalid"
            class="text-danger d-block small font-weight-bold font-italic mt-3"
          >
            (please fill this section as per guidelines instructions)
          </span>
        </h2>
        <div class="col pb-6 px-0 px-md-2">
          <div class="guidelines">
            <h4>Instructions</h4>
            <ul>
              <li>Note number of days will not be changed after form submision.</li>
              <li>The title should be at max. 50 characters long.</li>
              <li>
                Don't mention the number of days in the text box (e.g. 5-Day). Our system adds it automatically when you
                select the number of days.
              </li>
            </ul>
          </div>
          <div class="form-row col-md-10 align-items-center">
            <div class="form-group col-md-4 col-sm-12 mt-3">
              <select id="no_of_days" v-model="form.noOfDay" class="form-control form-control-sm">
                <option v-for="n in 75" :key="n" :value="n">{{ n }}-Day</option>
              </select>
            </div>
            <div class="form-group col-md-8 col-sm-12 mt-3">
              <input
                id="title"
                type="text"
                v-model="form.title"
                @input="$v.form.title.$touch()"
                class="form-control form-control-sm"
                placeholder="Title"
              />
            </div>
            <div v-if="form.noOfDay == 1" class="form-group col-md-4 col-sm-12 mt-3">
              <input class="form-control form-control-sm text-right" type="number" />
            </div>
            <div v-if="form.noOfDay == 1" class="form-group col-md-8 col-sm-12 mt-3">
              Hours<span class="badge badge-danger ml-2">In developing</span>
            </div>
          </div>
        </div>

        <!-- tour night input -->
        <h2 id="tour-night-sect" class="mt-6 text-primary"><strong>Tour night</strong></h2>
        <div class="col pb-6">
          <div class="guidelines">
            <h4>Instructions</h4>
            <ul>
              <li>Choose how many night this tour will provide.</li>
            </ul>
          </div>
          <div class="form-row">
            <div class="form-group" style="min-width: 200px">
              <select v-model="form.noOfNight" class="form-control form-control-sm">
                <option value="0">Night excluded</option>
                <option v-for="n in 75" :key="n" :value="n">{{ n }} - Night included</option>
              </select>
            </div>
          </div>
        </div>

        <!-- description input -->
        <h2 id="short-description-sect" class="mt-6 text-primary">
          <strong>Short tour description</strong>
          <span
            v-if="$v.form.description.$dirty && $v.form.description.$invalid"
            class="text-danger d-block small font-weight-bold font-italic mt-3"
          >
            (please fill this section as per guidelines instructions)
          </span>
        </h2>
        <div class="col pb-6 px-md-2 px-0">
          <div class="guidelines">
            <h4>Instructions</h4>
            <ul>
              <li>Don't include contact details or links.</li>
              <li>
                Write correct English: if English is not your first language we strongly recommend installing the free
                <a
                  href="https://support.grammarly.com/hc/en-us/articles/115000091552-Install-the-Grammarly-browser-extension"
                  target="_blank"
                >
                  Grammarly browser extension
                </a>
                and restarting your web browser. Once installed, Grammarly will check the text that you type in the
                tours form and offer suggestions to fix any errors.
              </li>
            </ul>
          </div>
          <div class="form-row col-md-10 px-md-2 px-0">
            <div class="form-group col mt-0">
              <vue-editor v-model="form.description" @input="$v.form.description.$touch()"></vue-editor>
              <!-- <ckeditor
                v-model="form.description"
                :config="CKE_config"
                @input="$v.form.description.$touch()"
              ></ckeditor> -->
            </div>
          </div>
        </div>

        <!-- tour standard input -->
        <h2 id="comfort-level-sect" class="mt-6 text-primary">
          Select <strong>comfort level</strong> of your tour
          <span
            v-if="$v.form.levels.$dirty && $v.form.levels.$invalid"
            class="text-danger d-block small font-weight-bold font-italic mt-3"
          >
            (please fill this section as per guidelines instructions)
          </span>
        </h2>
        <div class="col pb-6">
          <div class="guidelines">
            <h4>Instructions</h4>
            <ul>
              <li>Select choice of only one or two.</li>
            </ul>
          </div>
          <div class="form-row col-md-10">
            <ul class="col">
              <li class="row">
                <div class="form-group col-md-4 mt-0" v-for="(level, index) in levelList" :key="index">
                  <div class="form-check mb-2">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      v-model="form.levels"
                      @input="$v.form.levels.$touch()"
                      :id="'level-' + index"
                      :value="level.name"
                      :true-value="[]"
                    />
                    <label class="form-check-label" :for="'level-' + index">
                      <strong>{{ level.name | capitalizeWord }}</strong>
                    </label>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <!-- country input -->
        <h2 id="countries-sect" class="mt-6 text-primary">
          What <strong>countries</strong> will be visited on this tour
          <span
            v-if="$v.form.countries.$dirty && $v.form.countries.$invalid"
            class="text-danger d-block small font-weight-bold font-italic mt-3"
          >
            (please fill this section as per guidelines instructions)
          </span>
        </h2>
        <div class="col pb-6">
          <div class="guidelines">
            <h4>Instructions</h4>
            <ul>
              <li>
                We only accept tours if most of the itinerary takes place in one or more of these countries: Angola,
                Botswana, Burundi, Comoros, Congo (Brazzaville), Congo (DRC), Djibouti, Ethiopia, Kenya, Lesotho,
                Madagascar, Malawi, Mauritius, Mayotte, Mozambique, Namibia, Réunion, Rwanda, Seychelles, South Africa,
                Swaziland (Eswatini), Tanzania, Uganda, Zambia and/or Zimbabwe.
              </li>
            </ul>
          </div>
          <div class="form-row col-md-10">
            <ul class="col">
              <li class="row">
                <div class="form-group col-md-4 mt-0" v-for="c in countryList" :key="c.abr">
                  <div class="form-check mb-2">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      v-model="form.countries"
                      @input="$v.form.countries.$touch()"
                      :id="c.abr"
                      :value="c.name"
                      :true-value="[]"
                    />
                    <label class="form-check-label" :for="c.abr">
                      <strong>{{ c.name }}</strong>
                    </label>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <!-- main focus input -->
        <h2 id="main-focus-sect" class="mt-6 text-primary">What is the <strong>main focus</strong> of this tour</h2>
        <div class="col pb-6">
          <div class="guidelines">
            <h4>Instructions</h4>
            <ul>
              <li>Please read all options before making your selection.</li>
              <li>
                If more than one option applies, you should select the one that is most distinctive for this tour.
              </li>
              <li>
                The 'main focus' won't be visible on the Safari-Trek-Beach website, we only use it to let our system
                categorize tours.
              </li>
            </ul>
          </div>
          <div class="form-row col-md-10">
            <ul class="col">
              <li>
                <div class="form-group mt-0" v-for="f in focusList" :key="f.inputId" :id="f.inputId + '-div'">
                  <div class="form-check mb-2">
                    <input
                      type="radio"
                      class="form-check-input"
                      v-model="form.tourFocus"
                      :id="f.inputId"
                      :value="f.id"
                    />
                    <label class="form-check-label" :for="f.inputId">
                      <strong>{{ f.name }}</strong> {{ f.description ? "(" + f.description + ")" : "" }}
                    </label>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <!-- group input -->
        <h2 id="tour-group-sect" class="mt-6 text-primary">Is this a <strong>private or shared group</strong> tour</h2>
        <div class="col pb-6">
          <div class="guidelines">
            <h4>Instructions</h4>
            <ul>
              <li>
                A <b>private tour</b> is organized exclusively for a client and won't be shared with others. However,
                wildlife viewing activities at private camps/lodges are run by the lodges/camps and could be shared.
              </li>
              <li>
                On a <b>shared group tour</b> clients will join a group of other travelers. Clients will get a private
                room or tent, but the vehicle and activities will be shared with others.
              </li>
            </ul>
          </div>
          <div class="form-row col-md-10">
            <div class="form-group mt-0">
              <div class="form-check mb-2">
                <input
                  type="radio"
                  class="form-check-input"
                  v-model="form.tourGroup"
                  id="group-private"
                  value="private"
                />
                <label class="form-check-label" for="group-private">
                  <b>Private tour</b> (a private tour is organized exclusively for a client and won't be shared with
                  others)
                </label>
              </div>
            </div>

            <div class="form-group mt-0">
              <div class="form-check mb-2">
                <input type="radio" class="form-check-input" v-model="form.tourGroup" id="group-group" value="group" />
                <label class="form-check-label" for="group-group">
                  <b>Shared group tour</b>(on a shared group tour clients will join a group of other travelers.)
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- activities input -->
        <h2 id="destinations-activities-sect" class="mt-6 text-primary">
          What <strong>Destinations and Activities</strong> are included
          <span
            v-if="$v.form.activities.$dirty && $v.form.activities.$invalid"
            class="text-danger d-block small font-weight-bold font-italic mt-3"
          >
            (please fill this section as per guidelines instructions)
          </span>
        </h2>
        <div class="col pb-6">
          <div class="guidelines">
            <h4>Instructions</h4>
            <ul>
              <li>
                Select all Destinations and activities covered in this tour. Example 5 days marangu route climb ( You
                should select Mount Kilimanjaro, Marangu Route, Tanzania, Hike tour, Walking safari). Select as many
                options regarding your package because they are used as search filters. So, if you do not choose all
                options that your tour covers, then you risk not appearing in the specific results of that category.
                Example if you do not select marangu route then your tour covers marangu route do not expect to be seen
                on the search results.
              </li>
            </ul>
          </div>
          <div class="form-row activity-box col-md-10 mb-3">
            <span v-if="form.activities.length != 0" class="mr-3 my-2">Selected activities:</span>
            <span v-for="(item, index) in form.activities" :key="index" class="badge badge-success mr-3 my-2">
              {{
                tourActivities.find((option) => option.id == item) == undefined
                  ? ""
                  : tourActivities.find((option) => option.id == item).name
              }}
              <a @click="removeAcitivity(item)"><i class="icofont-close-line"></i></a>
            </span>
          </div>

          <div class="form-row activity-box col-md-10 mb-3">
            <input type="text" class="form-control" placeholder="Search activities and select" v-model="keyword" />
          </div>
          <div class="form-row activity-box col-md-10">
            <ul class="col">
              <li class="row">
                <div
                  class="form-group col-md-4 mt-0"
                  v-for="(a, index) in activityList"
                  :key="a.inputId"
                  :id="a.inputId + '-div'"
                >
                  <div class="form-check mb-2">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      v-model="form.activities"
                      @input="$v.form.activities.$touch()"
                      :id="a.inputId + '-' + (index + 1)"
                      :value="a.id"
                      :true-value="[]"
                    />
                    <label class="form-check-label" :for="a.inputId + '-' + (index + 1)">
                      <strong>{{ a.name }}</strong>
                    </label>
                  </div>
                </div>
                <div v-if="activityList.length == 0" class="fst-italic">Activity is not found.</div>
              </li>
            </ul>
          </div>
        </div>

        <!-- other activities input -->
        <h2 id="main-destinations-sect" class="mt-6 text-primary">
          What are the <strong>Main Destinations</strong> for this tour
        </h2>
        <div class="col pb-6">
          <div class="guidelines">
            <h4>Instructions</h4>
            <ul>
              <li>Select main destinations covered in this tour. They are used as search filters.</li>
            </ul>
          </div>
          <div class="col-md-10">
            <div class="form-group mt-0" v-for="a in otherActivityList" :key="a.inputId" :id="a.inputId + '-div'">
              <div id="other-activity" class="form-check mb-2">
                <input
                  @click="otherActivityInput($event), kilimanjaroInfoChecked($event)"
                  type="checkbox"
                  class="form-check-input"
                  v-model="form.otherActivity"
                  :id="a.inputId"
                  :value="a.id"
                  :true-value="a.id"
                />
                <label class="form-check-label" :for="a.inputId">
                  <strong>{{ a.name }}</strong>
                </label>
              </div>
            </div>
          </div>
          <div v-if="kilimanjaroInformationChecked">
            <p class="mt-5 mb-3"><b>Kilimanjaro Information</b> (Select all that apply)</p>
            <div class="form-row col-md-10">
              <ul class="col">
                <li class="row">
                  <div class="form-group col-md-4 mt-0" v-for="info in kilimanjaroInformationList" :key="info.inputId">
                    <div class="form-check mb-2">
                      <input
                        type="checkbox"
                        class="form-check-input"
                        v-model="form.kilimanjaroInformation"
                        :id="info.inputId"
                        :value="info.id"
                        :true-value="[]"
                      />
                      <label class="form-check-label" :for="info.inputId">
                        <strong>{{ info.name }}</strong>
                      </label>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- transportation input -->
        <h2 id="transportation-types-sect" class="mt-6 text-primary">
          What <strong>transportation types</strong> are used to travel between destinations on this tour
          <span
            v-if="$v.form.transportation.$dirty && $v.form.transportation.$invalid"
            class="text-danger d-block small font-weight-bold font-italic mt-3"
          >
            (please fill this section as per guidelines instructions)
          </span>
        </h2>
        <div class="col pb-6">
          <div class="guidelines">
            <h4>Instructions</h4>
            <ul>
              <li>
                Don't select transportation types that are optional (all selected should be included for the rate
                advertised and used on this tour).
              </li>
            </ul>
          </div>
          <div class="form-row col-md-10">
            <ul class="col">
              <li class="row">
                <div class="form-group col-md-6 mt-0" v-for="t in transportList" :key="t.inputId">
                  <div class="form-check mb-2">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      v-model="form.transportation"
                      @input="$v.form.transportation.$touch()"
                      :id="t.inputId"
                      :value="t.id"
                      :true-value="[]"
                      @click="transportationChecked($event)"
                    />
                    <label class="form-check-label" :for="t.inputId">
                      <strong>{{ t.name }}</strong> {{ t.description ? "(" + t.description + ")" : "" }}
                    </label>
                  </div>
                </div>
              </li>
            </ul>
          </div>
          <div v-if="safariJeepChecked">
            <p class="mt-3 mb-3"><b>Safari jeep facilities</b> (Select all that apply)</p>
            <div class="form-row col-md-10">
              <ul class="col">
                <li class="row">
                  <div
                    class="form-group col-md-4 mt-0"
                    v-for="vehicle in safariVehicle.safariJeep"
                    :key="vehicle.inputId"
                  >
                    <div class="form-check mb-2">
                      <input
                        type="checkbox"
                        class="form-check-input"
                        v-model="form.safariJeepFacility"
                        :id="vehicle.inputId"
                        :value="vehicle.id"
                        :true-value="[]"
                      />
                      <label class="form-check-label" :for="vehicle.inputId">
                        <strong>{{ vehicle.name }}</strong>
                      </label>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Getting there input -->
        <h2 id="getting-there-sect" class="mt-6 text-primary">
          <strong>Getting there &amp; away</strong>
          <span
            v-if="
              ($v.form.gettingThere.startCity.$dirty && $v.form.gettingThere.startCity.$invalid) ||
              ($v.form.gettingThere.endCity.$dirty && $v.form.gettingThere.endCity.$invalid)
            "
            class="text-danger d-block small font-weight-bold font-italic mt-3"
          >
            (please fill this section as per guidelines instructions)
          </span>
        </h2>
        <div class="col pb-6 px-0 px-md-2">
          <div class="guidelines">
            <h4>Instructions</h4>
            <ul>
              <li>
                List <b>only one</b> city per text box (don't list airports and don't add extra words such as 'city',
                'town' or a country name).
              </li>
              <li>
                Tours perform better if they start and end in a city near a major airport. If this isn’t the case, you
                could consider to extend your tour and include accommodation in- and transport from/to the city.
              </li>
            </ul>
          </div>
          <div class="form-row align-items-center ml-3">
            <div class="col-md-2 col-sm-4 mt-3">Starts in</div>
            <div class="form-group col-md-4 col-sm-8 mt-3">
              <input
                id="start-city"
                type="text"
                class="form-control form-control-sm"
                v-model="form.gettingThere.startCity"
                @change="form.gettingThere.startCity = $event.target.value"
                @input="$v.form.gettingThere.startCity.$touch()"
                autocorrect="off"
                autocomplete="off"
                data-autocomplete="city"
                placeholder="City"
              />
            </div>
            <div class="col-md-6 col-sm-12 mt-3">(The city from where the tour starts)</div>
          </div>
          <div class="form-row align-items-center ml-3">
            <div class="col-md-2 col-sm-4 mt-3">Ends in</div>
            <div class="form-group col-md-4 col-sm-8 mt-3">
              <input
                id="end-city"
                type="text"
                class="form-control form-control-sm"
                v-model="form.gettingThere.endCity"
                @change="form.gettingThere.endCity = $event.target.value"
                @input="$v.form.gettingThere.endCity.$touch()"
                autocorrect="off"
                autocomplete="off"
                data-autocomplete="city"
                placeholder="City"
              />
            </div>
            <div class="col-md-6 col-sm-12 mt-3">(The city where the tour ends)</div>
          </div>

          <p class="mt-5 mb-3">
            <b>International and Local airport to land</b>
          </p>
          <div class="form-row align-items-center col-md-10 mt-3 ml-3">
            <div class="mt-2">The best international airport to land is</div>
            <div class="form-group col-6 mt-2" style="min-width: 200px">
              <!-- <select v-model="form.gettingThere.internationalAirport" class="form-control form-control-sm">
                <option
                  v-for="airport in airportTanzaniaInternationalList"
                  :key="airport.inputId"
                  :value="airport.inputId"
                >
                  {{ airport.name }} <span v-if="airport.code">({{ airport.code }})</span>
                </option>
              </select> -->
              <InputTagDropdownAirportInter :inputData.sync="form.gettingThere.internationalAirport" />
            </div>
          </div>
          <div class="form-row align-items-center col-md-10 mt-3 ml-3">
            <div class="mt-2">The closest local or international airport to land is</div>
            <div class="form-group col-6 mt-2" style="min-width: 200px">
              <!-- <select v-model="form.gettingThere.localInternationalAirport" class="form-control form-control-sm">
                <option v-for="airport in airportTanzaniaList" :key="airport.inputId" :value="airport.inputId">
                  {{ airport.name }} <span v-if="airport.code">({{ airport.code }})</span>
                </option>
              </select> -->
              <InputTagDropdownAirportLocal :inputData.sync="form.gettingThere.localInternationalAirport" />
            </div>
          </div>

          <p class="mt-5 mb-3">
            <b>Can you help clients book flights?</b>
          </p>
          <div class="form-group mt-0 ml-4">
            <div class="form-check mb-2">
              <input
                type="radio"
                class="form-check-input"
                v-model="form.gettingThere.bookFlight"
                id="book-flight-yes"
                value="client-cost-yes"
              />
              <label for="book-flight-yes">
                <b>Yes,</b>
                <span> we can help clients book local and international flights (costs will be paid by client).</span>
              </label>
            </div>
          </div>
          <div class="form-group mt-0 ml-4">
            <div class="form-check mb-2">
              <input
                type="radio"
                class="form-check-input"
                v-model="form.gettingThere.bookFlight"
                id="book-flight-local"
                value="local-yes"
              />
              <label for="book-flight-local">
                <b>Yes,</b>
                <span>we can help clients book local flights but not international flights.</span>
              </label>
            </div>
          </div>
          <div class="form-group mt-0 ml-4">
            <div class="form-check mb-2">
              <input
                type="radio"
                class="form-check-input"
                v-model="form.gettingThere.bookFlight"
                id="book-flight-no"
                value="no"
              />
              <label for="book-flight-no">
                <b>No,</b>
                <span>we can not help clients book flights.</span>
              </label>
            </div>
          </div>

          <p class="mt-5 mb-3">
            <b>Is a transfer from and back to the airport or hotel included</b> (Tours with transfer perform better)
          </p>
          <div class="form-group mt-0 ml-4">
            <div class="form-check mb-2">
              <input
                type="radio"
                class="form-check-input"
                v-model="form.gettingThere.airportTransfer"
                id="roundtrip-incl"
                value="yes"
              />
              <label for="roundtrip-incl">
                <b>Yes,</b>
                <span>a transfer from and back to the airport or hotel is included.</span>
              </label>
            </div>
          </div>
          <div class="form-group mt-0 ml-4">
            <div class="form-check mb-2">
              <input
                type="radio"
                class="form-check-input"
                v-model="form.gettingThere.airportTransfer"
                id="roundtrip-optional"
                value="optional"
              />
              <label for="roundtrip-optional">
                <b>No,</b>
                <span>is not included, but can be arranged for an extra cost.</span>
              </label>
            </div>
          </div>
          <div class="form-group mt-0 ml-4">
            <div class="form-check mb-2">
              <input
                type="radio"
                class="form-check-input"
                v-model="form.gettingThere.airportTransfer"
                id="roundtrip-excl"
                value="no"
              />
              <label for="roundtrip-excl">
                <b>No,</b>
                <span>it is not included and We can’t Arrange Airport Transfers.</span>
              </label>
            </div>
          </div>

          <p class="mt-5 mb-3">
            <b>Can you arrange additional accommodation before and after the tour</b>
          </p>
          <div class="form-group mt-0 ml-4">
            <div class="form-check mb-2">
              <input
                type="radio"
                class="form-check-input"
                v-model="form.gettingThere.accomBeforeAfter"
                id="extra-cost"
                value="extra-cost"
              />
              <label for="extra-cost">
                <span>Additional accommodation can be arranged for an extra cost.</span>
              </label>
            </div>
          </div>
          <div class="form-group mt-0 ml-4">
            <div class="form-check mb-2">
              <input
                type="radio"
                class="form-check-input"
                v-model="form.gettingThere.accomBeforeAfter"
                id="before-yes"
                value="before-yes"
              />
              <label for="before-yes">
                <span>
                  Accommodation before safari is included. (after safari, accommodation can be arranged at an extra
                  cost).
                </span>
              </label>
            </div>
          </div>
          <div class="form-group mt-0 ml-4">
            <div class="form-check mb-2">
              <input
                type="radio"
                class="form-check-input"
                v-model="form.gettingThere.accomBeforeAfter"
                id="before-after-yes"
                value="yes"
              />
              <label for="before-after-yes">
                <span>Accommodation before and after safari is included.</span>
              </label>
            </div>
          </div>
          <div class="form-group mt-0 ml-4">
            <div class="form-check mb-2">
              <input
                type="radio"
                class="form-check-input"
                v-model="form.gettingThere.accomBeforeAfter"
                id="before-after-no"
                value="no"
              />
              <label for="before-after-no">
                <span>We Can’t arrange accommodation before and after the tour.</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- form step 2 -->
      <div v-if="formStep === 1 && form2 != undefined">
        <h4>All requested info and editorial instructions are required</h4>

        <!-- Day-by-Day input -->
        <h2 id="day-by-Day-sect" class="mt-6 text-primary">
          <strong>Day-by-Day</strong><span> (Click a day and provide the requested info)</span>
        </h2>
        <div v-if="isDayError" class="text-danger mt-3 ml-3">
          <span class="icofont-warning text-danger"></span>
          You should add {{ form2.noOfDay }} itinerary(s).
        </div>

        <div class="col pb-6 px-0 px-md-2">
          <div class="form-row col mt-4 px-0 px-md-2">
            <div id="accordion" class="w-100">
              <draggable v-model="form2.dayByDay" group="DayByDay" @start="drag = true" @end="drag = false">
                <transition-group>
                  <div class="card shadow-none mb-3" v-for="(day, dayNo) in dayByDay" :key="day.id">
                    <div class="card-header py-0" :id="'heading' + dayNo" v-if="form2.dayByDay[dayNo] != undefined">
                      <h5 class="d-flex">
                        <button
                          @click="navigateDay(dayNo)"
                          :id="'day-btn-' + dayNo"
                          type="button"
                          class="btn btn-sm btn-link w-100 text-left"
                          data-toggle="collapse"
                          :data-target="'#collapse' + dayNo"
                          aria-expanded="true"
                          :aria-controls="'collapse' + dayNo"
                          style="white-space: normal"
                        >
                          {{ form2.dayByDay[dayNo].day }} {{ form2.dayByDay[dayNo].title }}
                        </button>
                        <button
                          v-if="dayNo > 0"
                          @click="removeDayByDay(dayNo)"
                          class="btn btn-danger btn-sm ml-3"
                          type="button"
                        >
                          <i class="icofont-minus-circle"></i> remove
                        </button>
                      </h5>
                    </div>
                    <div
                      :id="'collapse' + dayNo"
                      class="collapse hide"
                      :aria-labelledby="'heading' + dayNo"
                      data-parent="#accordion"
                      v-if="form2.dayByDay[dayNo] != undefined"
                    >
                      <div class="card-body">
                        <p class="mb-3">
                          <b>Title for this part of the tour</b>
                        </p>
                        <div class="form-row align-items-center col-md-8">
                          <div class="form-group col-4 mt-0">
                            <input
                              type="text"
                              class="form-control form-control-sm"
                              v-model="form2.dayByDay[dayNo].day"
                              @change="day.day = $event.target.value"
                              placeholder="Day"
                            />
                          </div>
                          <div class="form-group col mt-0">
                            <input
                              type="text"
                              class="form-control form-control-sm"
                              v-model="form2.dayByDay[dayNo].title"
                              @change="day.title = $event.target.value"
                              placeholder="Title"
                            />
                          </div>
                        </div>

                        <p class="mt-5 mb-3">
                          <b>What destination is the highlight of this part of the tour</b>
                        </p>
                        <div class="guidelines">
                          <h4>Instructions</h4>
                          <ul>
                            <li>
                              List <b>only one</b> park, attraction, city or other destination (only enter the
                              destination name, don't add extra words such as 'city' or 'town').
                            </li>
                            <li>
                              Don't list <b>accommodations, countries, activities</b> or <b>transfers</b> (e.g. Arusha
                              to Ngorongoro). For transfers only list the destination.
                            </li>
                            <li>The destination you provide here will be used as <b>keyword for this tour</b>.</li>
                          </ul>
                        </div>
                        <div class="form-row align-items-center col-md-8">
                          <div class="form-group col-md-2 col-sm-12 mt-2">Destination:</div>
                          <div class="form-group col-md-10 col-sm-12 mt-2">
                            <InputTagDropdownDestination
                              :inputData.sync="form2.dayByDay[dayNo].destination"
                              @onChange="bannerFilter(dayNo), $v.form2.dayByDay.$each[dayNo].destination.$touch()"
                            />
                            <!-- <select
                              :id="'day-destination-' + dayNo"
                              class="form-control form-control-sm"
                              v-model="form2.dayByDay[dayNo].destination"
                              @change="bannerFilter(dayNo), $v.form2.dayByDay.$each[dayNo].destination.$touch()"
                            >
                              <option value="">--- select destination ---</option>
                              <option
                                v-for="(destination, index) in dayDestination"
                                :key="index"
                                :value="destination.name"
                              >
                                {{ destination.name }}
                              </option>
                            </select> -->
                            <span
                              v-if="
                                $v.form2.dayByDay.$each[dayNo].destination.$dirty &&
                                $v.form2.dayByDay.$each[dayNo].destination.$invalid
                              "
                              class="text-danger d-block small"
                            >
                              This field is required
                            </span>
                          </div>
                        </div>
                        <div class="form-row col mt-4">
                          <div class="form-group col-md-2 col-sm-12 mt-2">Photo:</div>
                          <div class="form-group col-md-10 col-sm-12 mt-2">
                            <input :id="'day-photo-' + dayNo" type="hidden" v-model="form2.dayByDay[dayNo].image" />

                            <div
                              :id="'destination-text-' + dayNo"
                              class="destination-photo-holder-txt"
                              style="display: none"
                            >
                              We don't have a photo for this destination
                            </div>
                            <div
                              :id="'banner-select-' + dayNo"
                              class="destination-photo-holder-txt"
                              :style="!form2.dayByDay[dayNo].image ? 'display: block;' : 'display: none;'"
                            >
                              Please select a destination first
                            </div>
                            <div class="mt-3 row">
                              <a
                                :id="'banner-photo-' + dayNo"
                                style="display: none"
                                @click="imageSelect($event, 'tour-day-gallery')"
                                class="select-thumb col-md-4 mt-3 col-sm-12"
                                :href="'#' + dayNo"
                              >
                                Select photo for only destination
                              </a>
                              <a
                                @click="imageSelectNew($event, 'tour-day-gallery')"
                                class="select-thumb col-md-4 mt-3 col-sm-12"
                                :href="'#' + dayNo"
                              >
                                Select photo from all library
                              </a>

                              <label
                                class="select-thumb col-md-4 mt-3 col-sm-12"
                                style="display: inline-block; color: #666666"
                              >
                                <input
                                  @change="handleTourDayImage($event, dayNo)"
                                  type="file"
                                  style="display: none"
                                  accept="image/png, image/gif, image/jpeg"
                                />
                                Upload your own photo
                              </label>
                            </div>
                          </div>
                        </div>

                        <div class="w-100 text-center">
                          <img
                            v-if="!form2.dayByDay[dayNo].image"
                            :id="'tour-bannerday-preview-' + dayNo"
                            src="@/assets/images/no-thumbnail-banner.jpg"
                            alt="img"
                            class="img-border mt-3"
                            style="width: 100%; max-width: 700px"
                          />
                          <img
                            v-else
                            :id="'tour-bannerday-preview-' + dayNo"
                            :src="form2.dayByDay[dayNo].image"
                            alt="img"
                            class="img-border mt-3"
                            style="width: 100%; max-width: 700px"
                          />
                        </div>

                        <div class="text-center mt-3" v-if="isUploading">Uploading image now, please wait...</div>
                        <div class="text-center text-danger mt-3" v-if="isUploadingFailed">
                          Uploading failed. Please check file type and try again.
                        </div>

                        <p class="mt-5 mb-3">
                          <b>Included accommodation</b>
                        </p>
                        <div class="form-check my-2 mt-5 ml-3">
                          <input
                            type="checkbox"
                            class="form-check-input"
                            :id="'day-no-accom-' + dayNo"
                            @change="noAccommodation(dayNo)"
                            v-model="isNoAccom[dayNo]"
                          />
                          <label class="form-check-label" :for="'day-no-accom-' + dayNo"> No accommodation </label>
                        </div>

                        <div v-if="!isNoAccom[dayNo]">
                          <div :id="'accom-day-' + dayNo" class="mt-2">
                            <div class="form-row justify-content-center">
                              <div class="row align-items-center">
                                <div class="form-group mt-3 col-md-6 col-sm-12">
                                  <div class="form-check mb-2" style="width: 200px">
                                    <input
                                      type="radio"
                                      class="form-check-input"
                                      v-model="form2.dayByDay[dayNo].camping"
                                      :id="'camping-no-' + dayNo"
                                      :value="false"
                                    />
                                    <label class="form-check-label" :for="'camping-no-' + dayNo"
                                      ><b>Accommodation</b></label
                                    >
                                  </div>
                                </div>
                                <div class="form-group mt-3 col-md-6 col-sm-12">
                                  <div class="form-check mb-2">
                                    <input
                                      type="radio"
                                      class="form-check-input"
                                      v-model="form2.dayByDay[dayNo].camping"
                                      :id="'camping-yes-' + dayNo"
                                      :value="true"
                                    />
                                    <label class="form-check-label" :for="'camping-yes-' + dayNo"><b>Camping</b></label>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <!-- :style="!form2.dayByDay[dayNo].camping ? 'display: flex;' : 'display: none;'" -->
                            <div
                              class="form-row justify-content-center col-md-10 my-3"
                              :data-accom-rating="'accom-' + dayNo"
                              v-if="form2.dayByDay[dayNo].camping == false"
                            >
                              <input type="hidden" v-model="form2.dayByDay[dayNo].accomRating" />
                              <star-rating
                                v-model="form2.dayByDay[dayNo].accomRating"
                                :show-rating="false"
                                :star-size="30"
                                :border-width="4"
                                border-color="#d8d8d8"
                                :rounded-corners="true"
                                :star-points="[
                                  23, 2, 14, 17, 0, 19, 10, 34, 7, 50, 23, 43, 38, 50, 36, 34, 46, 19, 31, 17,
                                ]"
                              ></star-rating>
                            </div>
                            <div
                              class="p-2 p-md-3 mt-3"
                              :data-camping="'yes-' + dayNo"
                              v-if="form2.dayByDay[dayNo].camping"
                              style="margin: auto; max-width: 600px; border: 1px dashed #535353"
                            >
                              <h4>Please don't confuse 'Camping' with 'Tented Camp'.</h4>
                              <div class="row mt-3 align-items-center justify-content-center">
                                <div class="col-md-4 col-sm-12">
                                  <img src="@/assets/images/camping.jpg" style="margin: auto" />
                                </div>
                                <div class="col-md-8 col-sm-12 text-sm-left text-center">
                                  <b>Camping</b><br />Select 'Camping' if tents are set up and taken down at each
                                  location and move with clients during their trip.
                                </div>
                              </div>

                              <div class="row mt-3 align-items-center justify-content-center">
                                <div class="col-md-4 col-sm-12">
                                  <img src="@/assets/images/tented-camp.jpg" style="margin: auto" />
                                </div>
                                <div class="col-md-8 col-sm-12 text-sm-left text-center">
                                  <b>Tented Camp</b><br />Select 'Tented Camp' if tents stay on location for months or
                                  longer and have facilities similar to a hotel room.
                                </div>
                              </div>
                            </div>

                            <div
                              class="form-row col-md-8"
                              :data-camping="'no-' + dayNo"
                              v-if="form2.dayByDay[dayNo].camping == false"
                            >
                              <div class="form-group col-3 pt-2">Name:</div>
                              <div class="form-group col">
                                <InputTagDropdownAccom
                                  :inputData.sync="form2.dayByDay[dayNo].accomName"
                                  @onChange="accomSelect(dayNo)"
                                />
                                <!-- <input
                                  type="text"
                                  class="form-control form-control-sm"
                                  v-model="form2.dayByDay[dayNo].accomName"
                                  placeholder="Accommodation"
                                /> -->
                                <span
                                  >Don't enter a hotel group (e.g. Serena Hotels), instead enter a specific
                                  accommodation (e.g. Selous Serena Camp)</span
                                >
                              </div>
                            </div>
                            <div
                              class="form-row col-md-8"
                              :data-camping="'yes-' + dayNo"
                              v-if="form2.dayByDay[dayNo].camping"
                            >
                              <div class="form-group col-3 pt-2">Name:</div>
                              <div class="form-group col">
                                <input
                                  type="text"
                                  class="form-control form-control-sm"
                                  v-model="form2.dayByDay[dayNo].campName"
                                  placeholder="Camping"
                                />
                              </div>
                            </div>

                            <div
                              class="form-row align-items-center col-md-8"
                              :data-camping="'no-' + dayNo"
                              v-if="form2.dayByDay[dayNo].camping == false"
                            >
                              <div class="form-group col-3">Type:</div>
                              <div class="form-group col">
                                <select
                                  :id="'days-accom-' + dayNo"
                                  v-model="form2.dayByDay[dayNo].accomType"
                                  class="form-control form-control-sm"
                                >
                                  <option disabled value="">-- Select accommodation type --</option>
                                  <option v-for="(type, index) in accomTypeList" :key="index" :value="type.name">
                                    {{ type.name }}
                                  </option>
                                </select>
                              </div>
                            </div>
                            <div
                              class="form-row align-items-center col-md-8"
                              :data-camping="'yes-' + dayNo"
                              v-if="form2.dayByDay[dayNo].camping"
                            >
                              <div class="form-group col-3">Type:</div>
                              <div class="form-group col">
                                <select
                                  :id="'days-camp-' + dayNo"
                                  v-model="form2.dayByDay[dayNo].campType"
                                  class="form-control form-control-sm"
                                >
                                  <option disabled value="">-- Select camp type --</option>
                                  <option v-for="(type, index) in campTypeList" :key="index" :value="type.name">
                                    {{ type.name | capitalizeWord }} ({{ type.description }})
                                  </option>
                                </select>
                              </div>
                            </div>
                            <div class="form-row align-items-center col-md-8">
                              <div class="form-group col-3">Comfort level:</div>
                              <div class="form-group col">
                                <select
                                  :id="'days-accom-level-' + dayNo"
                                  v-model="form2.dayByDay[dayNo].accomLevel"
                                  class="form-control form-control-sm"
                                >
                                  <option disabled value="">-- Select comfort level --</option>
                                  <option v-for="(level, index) in accomLevelList" :key="index" :value="level.name">
                                    {{ level.name }}
                                  </option>
                                </select>
                              </div>
                            </div>
                            <div class="form-row align-items-center col-md-8">
                              <div class="form-group col-3">Location:</div>
                              <div class="form-group col">
                                <select
                                  :id="'days-accom-location-' + dayNo"
                                  v-model="form2.dayByDay[dayNo].accomLocation"
                                  class="form-control form-control-sm"
                                >
                                  <option disabled value="">-- Select location relative to the park --</option>
                                  <option
                                    v-for="(location, index) in accomLocationList"
                                    :key="index"
                                    :value="location.value"
                                  >
                                    {{ location.label }}
                                  </option>
                                </select>
                              </div>
                            </div>
                            <div
                              class="form-row col-md-8"
                              :data-camping="'yes-' + dayNo"
                              :style="form2.dayByDay[dayNo].camping ? 'display: flex;' : 'display: none;'"
                            >
                              <div class="form-group col-3 pt-2">Participation:</div>
                              <div class="form-group col">
                                <div class="form-check mb-2">
                                  <input
                                    type="radio"
                                    class="form-check-input"
                                    v-model="form2.dayByDay[dayNo].accomParticipation"
                                    :id="'day-accom-part-yes-' + dayNo"
                                    value="true"
                                  />
                                  <label :for="'day-accom-part-yes-' + dayNo">
                                    <b>This is a semi (or full) participation tour</b>
                                    <span> (clients have to assist with camp chores)</span>
                                  </label>
                                </div>
                                <div class="form-check mb-2">
                                  <input
                                    type="radio"
                                    class="form-check-input"
                                    v-model="form2.dayByDay[dayNo].accomParticipation"
                                    :id="'day-accom-part-no-' + dayNo"
                                    value="false"
                                  />
                                  <label :for="'day-accom-part-no-' + dayNo">
                                    <b>This is a non-participation tour</b>
                                    <span> (clients don't assist with camp chores)</span>
                                  </label>
                                </div>
                              </div>
                            </div>
                            <div
                              class="form-row col-md-8"
                              :data-camping="'no-' + dayNo"
                              v-if="form2.dayByDay[dayNo].camping == false"
                            >
                              <div class="form-group col-3 pt-2">Website:</div>
                              <div class="form-group col">
                                <input
                                  :id="'day-accom-website-' + dayNo"
                                  type="text"
                                  class="form-control form-control-sm"
                                  v-model="form2.dayByDay[dayNo].accomWebsite"
                                  @change="
                                    websiteTest($event, dayNo),
                                      (form2.dayByDay[dayNo].accomWebsite = $event.target.value)
                                  "
                                  placeholder="Url"
                                />
                                <div class="form-check my-2">
                                  <input
                                    type="checkbox"
                                    class="form-check-input"
                                    :id="'day-accom-no-website-' + dayNo"
                                    @change="websiteNo(dayNo)"
                                  />
                                  <label class="form-check-label" :for="'day-accom-no-website-' + dayNo">
                                    I can't provide a website address as this accommodation doesn't have a (working)
                                    website
                                  </label>
                                </div>
                              </div>
                            </div>
                            <div
                              class="form-row col-md-8"
                              :data-camping="'no-' + dayNo"
                              v-if="form2.dayByDay[dayNo].camping == false"
                            >
                              <div class="form-group col-3 pt-2">Test website:</div>
                              <div class="form-group col">
                                <a
                                  :id="'website-test-' + dayNo"
                                  class="btn btn-success btn-sm"
                                  href="#"
                                  target="_blank"
                                >
                                  Test Website Address
                                </a>
                                <span> (Recommended)</span>
                              </div>
                            </div>
                          </div>

                          <div v-if="form2.dayByDay[dayNo].camping == false">
                            <p class="mt-5 mb-3"><b>Accommodation Images</b></p>
                            <label class="select-thumb my-3" style="display: inline-block; color: #666666">
                              <input
                                type="file"
                                @change="handleAccomImage($event, dayNo)"
                                style="display: none"
                                accept="image/png, image/gif, image/jpeg"
                              />
                              Click to upload the relevant accommodation images
                            </label>
                            <div class="row" v-if="form2.dayByDay[dayNo].accomPhotos != undefined">
                              <div
                                v-for="(photo, index_p) in form2.dayByDay[dayNo].accomPhotos"
                                :key="photo"
                                class="mx-2"
                              >
                                <!-- <input type="hidden" v-model="form3.gallery[index]" /> -->
                                <div class="d-block mb-4 position-relative">
                                  <div class="thumbnail-delete" style="width: 100px">
                                    <i @click="deleteAccomImage(index_p, dayNo)" class="icofont-close-squared-alt"></i>
                                  </div>
                                  <img class="img-fluid img-thumbnail" width="100" :src="photo" />
                                </div>
                              </div>
                            </div>
                            <div
                              class="text-center my-3"
                              v-if="
                                form2.dayByDay[dayNo].accomPhotos != undefined &&
                                form2.dayByDay[dayNo].accomPhotos.length == 0
                              "
                            >
                              There are no accommodation images.
                            </div>
                            <div class="text-center" v-if="isUploading">Uploading image now, please wait...</div>
                            <div class="text-center" v-if="isDeleting">Deleting image now, please wait...</div>
                            <div class="text-center text-danger" v-if="isUploadingFailed">
                              Uploading failed. Please check file type.
                            </div>
                          </div>

                          <p class="mt-5 mb-3"><b>Included facilities</b> (Select all that apply)</p>
                          <div class="form-row col-md-10">
                            <ul class="col" style="max-height: unset">
                              <li class="row">
                                <div class="form-group col-md-4 mt-0" v-for="f in accomFacilityList" :key="f.inputId">
                                  <div class="form-check mb-2">
                                    <input
                                      type="checkbox"
                                      class="form-check-input"
                                      v-model="form2.dayByDay[dayNo].facilities"
                                      :id="f.inputId + '-' + dayNo"
                                      :value="f.id"
                                      :true-value="[]"
                                    />
                                    <label class="form-check-label" :for="f.inputId + '-' + dayNo">
                                      <strong>{{ f.name }}</strong>
                                    </label>
                                  </div>
                                </div>
                              </li>
                            </ul>
                          </div>
                        </div>
                        <p class="mt-5 mb-3"><b>Included meals and drinks</b> (Select all that apply)</p>
                        <div class="form-row col-md-10">
                          <ul class="col">
                            <li class="row">
                              <div class="form-group col-md-4 mt-0" v-for="m in mealList" :key="m.inputId">
                                <div class="form-check mb-2">
                                  <input
                                    type="checkbox"
                                    class="form-check-input"
                                    v-model="form2.dayByDay[dayNo].mealsDrinks"
                                    :id="m.inputId + '-' + dayNo"
                                    :value="m.id"
                                    :true-value="[]"
                                  />
                                  <label class="form-check-label" :for="m.inputId + '-' + dayNo">
                                    <strong>{{ m.name }}</strong>
                                  </label>
                                </div>
                              </div>
                            </li>
                          </ul>
                        </div>

                        <p class="mt-5 mb-3">
                          <b>Description for this part of the tour</b>
                          <span
                            v-if="
                              $v.form2.dayByDay.$each[dayNo].description.$dirty &&
                              $v.form2.dayByDay.$each[dayNo].description.$invalid
                            "
                            class="text-danger d-block small font-weight-bold font-italic mt-3"
                          >
                            (please fill this section as per guidelines instructions)
                          </span>
                        </p>
                        <div class="guidelines">
                          <h4>Instructions</h4>
                          <ul>
                            <li>Don't include links or contact details.</li>
                          </ul>
                        </div>
                        <div class="form-row col-md-10">
                          <div class="form-group col mt-0">
                            <vue-editor
                              v-model="form2.dayByDay[dayNo]['description']"
                              @input="$v.form2.dayByDay.$each[dayNo].description.$touch()"
                              :key="'CK' + dayNo"
                            ></vue-editor>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </transition-group>
              </draggable>
            </div>
            <button @click="addDayByDay()" class="btn btn-secondary btn-sm ml-3 mt-3" type="button">
              <i class="icofont-plus-circle"></i> Add a day
            </button>
          </div>
        </div>

        <!-- customized input -->
        <h2 id="tour-customized-sect" class="mt-6 text-primary">
          Can the accommodations, destinations and length of this tour be <strong>customized</strong>
        </h2>
        <div class="col pb-6">
          <div class="guidelines">
            <h4>Instructions</h4>
            <ul>
              <li>
                Please only select 'Yes' if clients can request any accommodation or destination in the countries
                visited on this tour.
              </li>
            </ul>
          </div>
          <div class="col-md-10">
            <div class="form-group mt-0">
              <div class="form-check mb-2">
                <input
                  type="radio"
                  class="form-check-input"
                  v-model="form2.customizable"
                  id="customizable-no"
                  value="false"
                />
                <label class="form-check-label" for="customizable-no">
                  <b>No,</b>
                  <span>the accommodations, destinations and length of this tour cannot be customized</span>
                </label>
              </div>
            </div>
            <div class="form-group mt-0">
              <div class="form-check mb-2">
                <input
                  type="radio"
                  class="form-check-input"
                  v-model="form2.customizable"
                  id="customizable-yes"
                  value="true"
                />
                <label class="form-check-label" for="customizable-yes">
                  <b>Yes,</b>
                  <span>the accommodations, destinations and length of this tour can be customized</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- new price step -->
      <div v-if="formStep === 2">
        <!-- Dates & Rates input -->
        <h2 id="dates-rates-sect" class="mt-6 text-primary"><strong>Dates & Rates</strong></h2>
        <div class="col pb-6">
          <div class="guidelines">
            <h4>Instructions</h4>
            <ul>
              <li>
                Note: Season from December to February for winter, March to May for spring, June to August for summer
                and September to November for autumn.
              </li>
            </ul>
          </div>
          <div class="form-row col-md-10">
            <div class="mt-2 w-100">
              <span><b>Rates for this tour</b> (Rates should be per person in USD ($)) <b>for Adult 16+</b></span>
              <div class="card mt-2 mb-5">
                <div class="table-responsive">
                  <table class="table table-sm table-nowrap card-table table-bordered table-striped">
                    <thead class="thead-dark">
                      <tr>
                        <th>Start dates</th>
                        <th>1 Person</th>
                        <th>2 Person</th>
                        <th>3 Person</th>
                        <th>4 Person</th>
                        <th>5 Person</th>
                        <th>6 Person</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Dec - Feb</td>
                        <td v-for="(n, index) in 6" :key="'adult-currency-winter-' + n">
                          <input
                            class="form-control form-control-sm"
                            type="number"
                            v-model="form2.rates.adultCurrencyWinter[index]"
                            min="0"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>Mar - May</td>
                        <td v-for="(n, index) in 6" :key="'adult-currency-spring-' + n">
                          <input
                            class="form-control form-control-sm"
                            type="number"
                            v-model="form2.rates.adultCurrencySpring[index]"
                            min="0"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>Jun - Aug</td>
                        <td v-for="(n, index) in 6" :key="'adult-currency-summer-' + n">
                          <input
                            class="form-control form-control-sm"
                            type="number"
                            v-model="form2.rates.adultCurrencySummer[index]"
                            min="0"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>Sept - Nov</td>
                        <td v-for="(n, index) in 6" :key="'adult-currency-autumn-' + n">
                          <input
                            class="form-control form-control-sm"
                            type="number"
                            v-model="form2.rates.adultCurrencyAutumn[index]"
                            min="0"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <span><b>Rates for this tour</b> (Rates should be per person in USD ($)) <b>for Child 4 - 15</b></span>
              <div class="card mt-2 mb-5">
                <div class="table-responsive">
                  <table class="table table-sm table-nowrap card-table table-bordered table-striped">
                    <thead class="thead-dark">
                      <tr>
                        <th>Start dates</th>
                        <th>1 Person</th>
                        <th>2 Person</th>
                        <th>3 Person</th>
                        <th>4 Person</th>
                        <th>5 Person</th>
                        <th>6 Person</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Dec - Feb</td>
                        <td v-for="(n, index) in 6" :key="'child-currency-winter-' + n">
                          <input
                            class="form-control form-control-sm"
                            type="number"
                            v-model="form2.rates.childCurrencyWinter[index]"
                            min="0"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>Mar - May</td>
                        <td v-for="(n, index) in 6" :key="'child-currency-spring-' + n">
                          <input
                            class="form-control form-control-sm"
                            type="number"
                            v-model="form2.rates.childCurrencySpring[index]"
                            min="0"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>Jun - Aug</td>
                        <td v-for="(n, index) in 6" :key="'child-currency-summer-' + n">
                          <input
                            class="form-control form-control-sm"
                            type="number"
                            v-model="form2.rates.childCurrencySummer[index]"
                            min="0"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>Sept - Nov</td>
                        <td v-for="(n, index) in 6" :key="'child-currency-autumn-' + n">
                          <input
                            class="form-control form-control-sm"
                            type="number"
                            v-model="form2.rates.childCurrencyAutumn[index]"
                            min="0"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div class="d-flex align-items-center">
                <span><b>This tour provide discount of </b></span>
                <div class="input-group col-2" style="min-width: 100px">
                  <input
                    type="number"
                    class="form-control form-control-sm"
                    v-model="form2.rates.discount"
                    aria-describedby="input-tour-discount"
                  />
                  <div class="input-group-append">
                    <span class="input-group-text p-0 px-2" id="input-tour-discount">%</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-4 w-100">
              <span>
                <b>From which date to which date will the tour be available</b> (for fixed start & end dates tour)
              </span>
              <div class="form-group row">
                <label for="rate-start" class="col-2 col-form-label" style="min-width: 105px">Start date</label>
                <div class="input-group col-4" style="min-width: 160px">
                  <input
                    class="form-control form-control-sm"
                    type="date"
                    v-model="form2.rates.startDate"
                    min="0"
                    id="rate-start"
                  />
                </div>
              </div>
              <div class="form-group row">
                <label for="rate-end" class="col-2 col-form-label" style="min-width: 105px">End date</label>
                <div class="input-group col-4" style="min-width: 160px">
                  <input
                    class="form-control form-control-sm"
                    type="date"
                    v-model="form2.rates.endDate"
                    min="0"
                    id="rate-end"
                  />
                </div>
              </div>
            </div>

            <div class="mt-5 w-100">
              <p class="mb-3">
                <b>Park Fees or Tickets cost (VAT Inclusive)</b>
              </p>
              <div class="d-flex align-items-center ml-4">
                <div class="form-group mt-0">
                  <div class="form-check mb-2">
                    <input
                      type="radio"
                      class="form-check-input"
                      v-model="form2.parkRates.parkFee"
                      id="park-fee-yes"
                      value="true"
                    />
                    <label class="form-check-label" for="park-fee-yes"><b>Yes</b></label>
                  </div>
                </div>
                <div class="form-group mt-0 ml-4">
                  <div class="form-check mb-2">
                    <input
                      type="radio"
                      class="form-check-input"
                      v-model="form2.parkRates.parkFee"
                      id="park-fee-no"
                      value="false"
                    />
                    <label class="form-check-label" for="park-fee-no"><b>No</b></label>
                  </div>
                </div>
              </div>
              <p class="my-3">The Park Fees or Tickets cost included in the cost of this tour.</p>

              <div class="mt-3 ml-4" v-if="JSON.parse(form2.parkRates.parkFee)">
                <div class="form-group" v-for="(fee, index) in parkTicketFeeList" :key="index">
                  <span>{{ fee.label }} </span>
                  <input type="number" v-model="form2.parkRates[fee.name]" />
                  <span> USD</span>
                </div>
              </div>
            </div>

            <div class="mt-5 w-100">
              <p class="mb-3">
                <b>Do you accept credit card payments?</b>
              </p>
              <div class="mt-3 ml-4">
                <div class="form-check mb-2">
                  <input
                    type="radio"
                    class="form-check-input"
                    v-model="form2.rates.cardPayment"
                    id="card-payment-yes"
                    value="yes"
                  />
                  <label class="form-check-label" for="card-payment-yes">
                    <b>Yes,</b><span> we do accept Credit Card Payments.</span>
                  </label>
                </div>
              </div>
              <div class="mt-3 ml-4">
                <div class="form-check mb-2">
                  <input
                    type="radio"
                    class="form-check-input"
                    v-model="form2.rates.cardPayment"
                    id="card-payment-no"
                    value="no"
                  />
                  <label class="form-check-label" for="card-payment-no">
                    <b>No,</b><span> we do not accept Credit Card Payments.</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- form step 3 -->
      <div v-if="formStep === 3">
        <h4>All requested info and editorial instructions are required</h4>

        <!-- Tour photo input -->
        <h2 id="tour-photo-sect" class="mt-6 text-primary">
          <strong>Tour photo</strong>
          <span
            v-if="
              ($v.form3.image.$dirty && $v.form3.image.$invalid) || ($v.form3.banner.$dirty && $v.form3.banner.$invalid)
            "
            class="text-danger d-block small font-weight-bold font-italic mt-3"
          >
            (please fill this section as per guidelines instructions)
          </span>
        </h2>
        <div class="col pb-6">
          <div class="guidelines">
            <h4>Instructions</h4>
            <ul>
              <li>Please select a photo that represents your tour.</li>
            </ul>
          </div>
          <div class="">
            <ul>
              <li>
                <p class="mb-3">
                  <b>Tour Package Photo</b>
                </p>
              </li>
              <li class="mb-4 row">
                <a
                  @click="imageSelect($event, 'tour-thumb'), $v.form3.image.$touch()"
                  class="select-thumb col-md-4 col-sm-12 mt-3"
                  href="#"
                >
                  Select photo for only destination
                </a>
                <a
                  @click="imageSelectNew($event, 'tour-thumb'), $v.form3.image.$touch()"
                  class="select-thumb col-md-4 col-sm-12 mt-3"
                  href="#"
                >
                  Select photo from all library
                </a>
                <label class="select-thumb col-md-4 col-sm-12 mt-3" style="display: inline-block; color: #666666">
                  <input
                    @change="handleTourImage($event)"
                    type="file"
                    style="display: none"
                    accept="image/png, image/gif, image/jpeg"
                  />
                  Upload your own photo
                </label>
              </li>
              <li>
                <a
                  @click="imageSelect($event, 'tour-thumb')"
                  id="select-thumb-block"
                  v-if="!form3.image"
                  class="d-flex flex-column select-thumb select-thumb-block"
                  href="#"
                >
                  <div class="col d-flex align-items-center justify-content-center">
                    <svg width="50" height="50" xmlns="http://www.w3.org/2000/svg">
                      <g fill="none" fill-rule="evenodd">
                        <path
                          d="M3.125 9.375h37.5v37.5c0 1.726-1.4 3.125-3.125 3.125H6.25A6.25 6.25 0 0 1 0 43.75V12.5c0-1.726 1.4-3.125 3.125-3.125z"
                          fill="#FFF"
                        ></path>
                        <path
                          d="M3.125 6.25H43.75v40.625H6.25a3.125 3.125 0 0 1-3.125-3.125V6.25z"
                          fill="#D2D2D2"
                        ></path>
                        <path
                          d="M12.5 0h31.25A6.25 6.25 0 0 1 50 6.25V37.5a6.25 6.25 0 0 1-6.25 6.25H9.375a3.125 3.125 0 0 1-3.125-3.125V6.25A6.25 6.25 0 0 1 12.5 0z"
                          fill="#FFF"
                        ></path>
                        <path
                          stroke="#D2D2D2"
                          fill="#D2D2D2"
                          stroke-linejoin="round"
                          d="M43.75 37.5H28.125l7.813-10.938z"
                        ></path>
                        <path
                          stroke="#D2D2D2"
                          fill="#D2D2D2"
                          stroke-linejoin="round"
                          d="M15.625 37.5l7.813-7.813L31.25 37.5z"
                        ></path>
                        <circle fill="#D2D2D2" cx="18.75" cy="18.75" r="3.125"></circle>
                      </g>
                    </svg>
                  </div>
                  <span class="col text-center text-white">Select a tour photo</span>
                </a>
                <img id="tour-thumb-preview" :src="form3.image" alt="img" class="img-border" />
                <input type="hidden" id="tour-thumb-url" v-model="form3.image" />
              </li>
            </ul>
            <div class="text-center mt-3" v-if="isUploading">Uploading image now, please wait...</div>
            <div class="text-center text-danger mt-3" v-if="isUploadingFailed">
              Uploading failed. Please check file type and try again.
            </div>
            <ul>
              <li>
                <p class="mt-5">
                  <b>Tour Inner Page (Photo or Video)</b>
                </p>
              </li>
              <li class="mb-4">
                <div class="form-row">
                  <div class="d-flex align-items-center">
                    <div class="form-group mt-0">
                      <div class="form-check mb-2">
                        <input
                          id="tour-banner-image"
                          type="radio"
                          class="form-check-input"
                          value="image"
                          v-model="form3.bannerType"
                          @click="tourBannerType('image')"
                        />
                        <label class="form-check-label" for="tour-banner-image"><b>Image</b></label>
                      </div>
                    </div>
                    <div class="form-group mt-0 ml-4">
                      <div class="form-check mb-2">
                        <input
                          id="tour-banner-video"
                          type="radio"
                          class="form-check-input"
                          value="video"
                          v-model="form3.bannerType"
                          @click="tourBannerType('video')"
                        />
                        <label class="form-check-label" for="tour-banner-video"><b>Video</b></label>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
              <li>
                <div v-if="form3.bannerType === 'image'" class="row">
                  <a
                    @click="imageSelect($event, 'tour-banner'), $v.form3.banner.$touch()"
                    class="select-banner mb-4 col-md-4 col-sm-12 mt-3"
                    href="#"
                  >
                    Select photo from only destination
                  </a>

                  <a
                    @click="imageSelectNew($event, 'tour-banner'), $v.form3.banner.$touch()"
                    class="select-banner mb-4 col-md-4 col-sm-12 mt-3"
                    href="#"
                  >
                    Select photo from all library
                  </a>

                  <label class="select-thumb col-md-4 col-sm-12 mt-3" style="display: inline-block; color: #666666">
                    <input
                      @change="handleTourInnerImage($event)"
                      type="file"
                      style="display: none"
                      accept="image/png, image/gif, image/jpeg"
                    />
                    Upload your own photo
                  </label>

                  <a
                    @click="imageSelect($event, 'tour-banner')"
                    id="select-banner-block"
                    v-if="!form3.banner"
                    class="d-flex flex-column select-banner select-banner-block"
                    href="#"
                  >
                    <div class="col d-flex align-items-center justify-content-center">
                      <svg width="50" height="50" xmlns="http://www.w3.org/2000/svg">
                        <g fill="none" fill-rule="evenodd">
                          <path
                            d="M3.125 9.375h37.5v37.5c0 1.726-1.4 3.125-3.125 3.125H6.25A6.25 6.25 0 0 1 0 43.75V12.5c0-1.726 1.4-3.125 3.125-3.125z"
                            fill="#FFF"
                          ></path>
                          <path
                            d="M3.125 6.25H43.75v40.625H6.25a3.125 3.125 0 0 1-3.125-3.125V6.25z"
                            fill="#D2D2D2"
                          ></path>
                          <path
                            d="M12.5 0h31.25A6.25 6.25 0 0 1 50 6.25V37.5a6.25 6.25 0 0 1-6.25 6.25H9.375a3.125 3.125 0 0 1-3.125-3.125V6.25A6.25 6.25 0 0 1 12.5 0z"
                            fill="#FFF"
                          ></path>
                          <path
                            stroke="#D2D2D2"
                            fill="#D2D2D2"
                            stroke-linejoin="round"
                            d="M43.75 37.5H28.125l7.813-10.938z"
                          ></path>
                          <path
                            stroke="#D2D2D2"
                            fill="#D2D2D2"
                            stroke-linejoin="round"
                            d="M15.625 37.5l7.813-7.813L31.25 37.5z"
                          ></path>
                          <circle fill="#D2D2D2" cx="18.75" cy="18.75" r="3.125"></circle>
                        </g>
                      </svg>
                    </div>
                    <span class="col text-center text-white">Select a tour photo</span>
                  </a>
                  <img id="tour-banner-preview" v-if="form3.banner" :src="form3.banner" alt="img" class="img-border" />
                  <input type="hidden" id="tour-banner-url" v-model="form3.banner" />
                </div>
                <div class="text-center mt-3" v-if="isUploading">Uploading image now, please wait...</div>
                <div class="text-center text-danger mt-3" v-if="isUploadingFailed">
                  Uploading failed. Please check file type and try again.
                </div>
                <div v-if="form3.bannerType === 'video'">
                  <video-embed
                    css="embed-responsive-21by9"
                    :params="{ showinfo: 0, controls: 0 }"
                    :src="form3.banner"
                  ></video-embed>
                  <input
                    type="text"
                    id="tour-banner-url"
                    class="form-control form-control-sm mt-3"
                    v-model="form3.banner"
                    @change="tourBannerVideo($event), $v.form3.banner.$touch()"
                    placeholder="https://www.youtube.com/watch?v=dY7IJ5Az4jQ"
                  />
                </div>
              </li>
            </ul>
          </div>
        </div>

        <!-- Photo gallery -->
        <h2 id="photo-gallery-sect" class="mt-6 text-primary">
          <strong>Photo gallery</strong>
          <span
            v-if="$v.form3.gallery.$dirty && $v.form3.gallery.$invalid"
            class="text-danger d-block small font-weight-bold font-italic mt-3"
          >
            (please fill this section as per guidelines instructions)
          </span>
        </h2>
        <div class="col pb-6">
          <div class="guidelines">
            <h4>Instructions</h4>
            <ul>
              <li>Select max 12 photos for your gallery.</li>
            </ul>
          </div>
          <div class="form-row">
            <a
              @click="imageSelect($event, 'tour-gallery'), $v.form3.gallery.$touch()"
              class="select-thumb mb-4 col-md-4 col-sm-12 mt-3"
              href="#"
            >
              Select photo from only destination
            </a>

            <a
              @click="imageSelectNew($event, 'tour-gallery'), $v.form3.gallery.$touch()"
              class="select-thumb mb-4 col-md-4 col-sm-12 mt-3"
              href="#"
            >
              Select photo from all library
            </a>
            <label class="select-thumb col-md-4 col-sm-12 mt-3" style="display: inline-block; color: #666666">
              <input
                @change="handleTourGalleryImage($event)"
                type="file"
                style="display: none"
                accept="image/png, image/gif, image/jpeg"
              />
              Upload your own photo
            </label>
          </div>

          <div class="row text-center w-100">
            <div v-for="(photo, index) in form3.gallery" :key="photo" class="col-lg-3 col-md-4 col-6">
              <input type="hidden" v-model="form3.gallery[index]" />
              <div class="d-block mb-4 position-relative">
                <div class="thumbnail-delete">
                  <i @click="galleryPhotoRemove(index)" class="icofont-close-squared-alt"></i>
                </div>
                <img class="img-fluid img-thumbnail" :src="photo" />
              </div>
            </div>
          </div>
          <div class="text-center mt-3" v-if="isUploading">Uploading image now, please wait...</div>
          <div class="text-center text-danger mt-3" v-if="isUploadingFailed">
            Uploading failed. Please check file type and try again.
          </div>
        </div>
      </div>

      <!-- form step 4 -->
      <div v-if="formStep === 4" :key="update">
        <h4>All requested info and editorial instructions are required</h4>

        <!-- Inclusions input -->
        <h2 id="inclusions-sect" class="mt-6 text-primary"><strong>Inclusions</strong></h2>

        <div class="col pb-6 mt-4">
          <div class="guidelines">
            <h4>Instructions</h4>
            <ul>
              <li>
                Please select only the relevant inclusions and exclusions reflecting your package. If the option is not
                included nor excluded please select not relevant.
              </li>
            </ul>
          </div>
          <div class="form-row" v-for="(note, index) in form4.inclusion" :key="'note' + index">
            <div class="d-flex align-items-center">
              <div class="form-group mt-0">
                <div class="form-check mb-2">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :id="note.tourNote + '-included'"
                    :true-value="true"
                    :false-value="false"
                    v-model="form4.inclusion[index].included"
                  />
                  <label class="form-check-label" :for="note.tourNote + '-included'"><b>Included</b></label>
                </div>
              </div>
              <div class="form-group mt-0 ml-4">
                <div class="form-check mb-2">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :id="note.tourNote + '-excluded'"
                    :true-value="true"
                    :false-value="false"
                    v-model="form4.inclusion[index].excluded"
                  />
                  <label class="form-check-label" :for="note.tourNote + '-excluded'"><b>Excluded</b></label>
                </div>
              </div>
              <div class="form-group mt-0 ml-4" style="min-width: 120px">
                <div class="form-check mb-2">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :id="note.tourNote + '-notrelevant'"
                    value="notrelevant"
                  />
                  <label class="form-check-label" :for="note.tourNote + '-notrelevant'"><b>Not Relevant</b></label>
                </div>
              </div>
              <span class="mb-1 ml-4">
                {{ note.name }} {{ note.description ? "(" + note.description + ")" : "" }}
              </span>
            </div>
          </div>
          <h5>
            Additional inclusion and exclusions for this package <span class="badge badge-info">In development</span>
          </h5>
          <h6>- Inclusions</h6>
          <div
            v-for="(include, index) in extraInclusion"
            :key="index"
            class="mb-2 mb-2 d-flex align-items-center"
            style="max-width: 300px"
          >
            <input v-model="extraInclusion[index]" class="form-control form-control-sm" />
            <a @click="removeExtraInclusion(index)"><i class="icofont-close-line"></i></a>
          </div>
          <a class="btn btn-primary my-3" @click="addExtraInclusion">Add inclusion</a>
          <h6>- Exclusions</h6>
          <div
            v-for="(include, index) in extraExclusion"
            :key="index"
            class="mb-2 mb-2 d-flex align-items-center"
            style="max-width: 300px"
          >
            <input v-model="extraExclusion[index]" class="form-control form-control-sm" />
            <a @click="removeExtraExclusion(index)"><i class="icofont-close-line"></i></a>
          </div>
          <a class="btn btn-primary my-3" @click="addExtraExclusion">Add exclusion</a>
        </div>

        <!-- Notes & disclaimers input -->
        <h2 id="notes-disclaimers-sect" class="mt-6 text-primary"><strong>Notes & disclaimers</strong></h2>
        <div class="col pb-6">
          <div class="guidelines">
            <h4>Instructions</h4>
            <ul>
              <li>
                Select 'No' if you don't want these notes to be visible to people who view your tour page on
                Safari-Trek-Beach.
              </li>
            </ul>
          </div>
          <div class="form-row col-md-10" v-for="note in noteList" :key="note.inputId">
            <div class="d-flex align-items-center" v-if="note.name == 'Disclaimer'">
              <div class="form-group mt-0">
                <div class="form-check mb-2">
                  <input
                    type="radio"
                    class="form-check-input"
                    :name="note.inputId"
                    :id="note.inputId + '-yes'"
                    :value="{ tourNote: note.id, accept: 'yes' }"
                    v-model="form4.disclaimers[note.inputId]"
                  />
                  <label class="form-check-label" :for="note.inputId + '-yes'"><b>Yes</b></label>
                </div>
              </div>
              <div class="form-group mt-0 ml-4">
                <div class="form-check mb-2">
                  <input
                    type="radio"
                    class="form-check-input"
                    :name="note.inputId"
                    :id="note.inputId + '-no'"
                    :value="{ tourNote: note.id, accept: 'no' }"
                    v-model="form4.disclaimers[note.inputId]"
                  />
                  <label class="form-check-label" :for="note.inputId + '-no'"><b>No</b></label>
                </div>
              </div>
              <span class="mb-1 ml-4">{{ note.description }}</span>
            </div>
          </div>
        </div>

        <!-- tour guides overview input -->
        <h2 id="guides-overview-sect" class="mt-6 text-primary">Select <strong>guides overview</strong></h2>
        <div class="col pb-6">
          <div class="guidelines">
            <h4>Instructions</h4>
            <ul>
              <li>Select choice of tour guides overview.</li>
            </ul>
          </div>
          <div class="form-row col-md-10">
            <ul class="col">
              <li class="row">
                <div class="form-group col-md-4 mt-0" v-for="overview in guideOverview" :key="overview.inputId">
                  <div class="form-check mb-2">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      :id="overview.inputId"
                      :value="overview.inputId"
                      :true-value="overview.inputId"
                      v-model="form4.guideOverview"
                    />
                    <label class="form-check-label" :for="overview.inputId">
                      <strong>{{ overview.name }}</strong>
                    </label>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <!-- tour meals type input -->
        <h2 id="meals-type-sect" class="mt-6 text-primary">Select <strong>meals type available</strong></h2>
        <div class="col pb-6">
          <div class="guidelines">
            <h4>Instructions</h4>
            <ul>
              <li>Select choice of tour meals type available.</li>
            </ul>
          </div>
          <div class="form-row col-md-10">
            <ul class="col">
              <li class="row">
                <div class="form-group col-md-4 mt-0" v-for="type in mealTypeList" :key="type.inputId">
                  <div class="form-check mb-2">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      :id="type.inputId"
                      :value="type.inputId"
                      :true-value="type.inputId"
                      v-model="form4.mealTypes"
                    />
                    <label class="form-check-label" :for="type.inputId">
                      <strong>{{ type.name }}</strong>
                    </label>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <!-- tour vaccines input -->
        <h2 id="vaccines-sect" class="mt-6 text-primary">Select <strong>vaccines recommended</strong></h2>
        <div class="col pb-6">
          <div class="guidelines">
            <h4>Instructions</h4>
            <ul>
              <li>Select choice of tour vaccines available.</li>
            </ul>
          </div>
          <div class="form-row col-md-10">
            <ul class="col">
              <li class="row">
                <div class="form-group col-md-4 mt-0" v-for="vaccine in vaccineList" :key="vaccine.inputId">
                  <div class="form-check mb-2">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      :id="vaccine.inputId"
                      :value="vaccine.inputId"
                      :true-value="vaccine.inputId"
                      v-model="form4.vaccines"
                    />
                    <label class="form-check-label" :for="vaccine.inputId">
                      <strong>{{ vaccine.name }}</strong>
                    </label>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <!-- Additional information input -->
        <h2 id="additional-information-sect" class="mt-6 text-primary"><strong>Additional information</strong></h2>
        <div class="col pb-6">
          <div class="form-row align-items-center col-md-10 mt-3">
            <div class="col-md-4 col-sm-12 mt-3">Free Tour Cancellation Up to</div>
            <div class="form-group col-md-3 col-sm-12 mt-3">
              <select class="form-control form-control-sm" v-model="form4.additionalInfo.cancellation">
                <option value="">Not Available</option>
                <option value="24-hrs">24 hrs</option>
                <option value="3-days">3 days</option>
                <option value="1-week">1 week</option>
                <option value="2-weeks">2 weeks</option>
                <option value="1-month">1 month</option>
                <option value="3-months">3 months</option>
                <option value="6-months">6 months</option>
              </select>
            </div>
          </div>
          <div class="form-row align-items-center col-md-10 mt-3">
            <div class="col-md-4 col-sm-12 mt-3">Tour Date Change</div>
            <div class="form-group col-sm-12 col-md-3 mt-3">
              <select class="form-control form-control-sm" v-model="form4.additionalInfo.freeDateChange">
                <option value="true">Available</option>
                <option value="false">Not Available</option>
              </select>
            </div>
          </div>
          <div class="form-row align-items-center col-md-10 mt-3">
            <div class="col-md-4 col-sm-12 mt-3">Can issue Credit note</div>
            <div class="form-group col-md-3 col-sm-12 mt-3">
              <select class="form-control form-control-sm" v-model="form4.additionalInfo.issueCreditNote">
                <option value="true">Available</option>
                <option value="false">Not Available</option>
              </select>
            </div>
          </div>
          <div class="form-row align-items-center col-md-10 mt-3">
            <div class="col-md-4 mt-3 col-sm-12">Tour Advance Payment only</div>
            <div class="form-group col-sm-12 col-md-3 mt-3">
              <select class="form-control form-control-sm" v-model="form4.additionalInfo.advancePayment">
                <option value="5%">5%</option>
                <option value="10%">10%</option>
                <option value="15%">15%</option>
                <option value="20%">20%</option>
                <option value="25%">25%</option>
                <option value="30%">30%</option>
                <option value="35%">35%</option>
                <option value="40%">40%</option>
                <option value="50%">50%</option>
                <option value="70%">70%</option>
              </select>
            </div>
          </div>
          <div class="form-row align-items-center col-md-10 mt-3">
            <div class="col-md-4 col-sm-12 mt-3">Tour Guaranteed Departure</div>
            <div class="form-group col-md-3 mt-3 col-sm-12">
              <select class="form-control form-control-sm" v-model="form4.additionalInfo.departure">
                <option value="true">Guaranteed</option>
                <option value="false">Non Guaranteed</option>
              </select>
            </div>
          </div>
          <div class="form-row align-items-center col-md-10 mt-3">
            <div class="col-md-4 col-sm-12 mt-3">Get Visa On Arrival</div>
            <div class="form-group col-md-3 mt-3 col-sm-12">
              <select class="form-control form-control-sm" v-model="form4.additionalInfo.arrivalVisa">
                <option value="true">Available</option>
                <option value="false">Not Available</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Why us input -->
        <h2 id="why-us-sect" class="mt-6 text-primary">
          <strong>Why Should Client Choose ({{ user.companyName }})</strong>
        </h2>
        <div class="col pb-6">
          <div class="guidelines">
            <h4>Instructions</h4>
            <ul>
              <li>Write a short description about your company for this tour.</li>
            </ul>
          </div>
          <div class="form-row col-md-10">
            <div class="form-group col mt-0">
              <!-- <ckeditor v-model="form4.whyUs" :config="CKE_config"></ckeditor> -->
              <vue-editor v-model="form4.whyUs"></vue-editor>
            </div>
          </div>
        </div>
      </div>

      <!-- form submit -->
      <div v-if="formStep < formSteps.length" class="col pb-4 mt-6">
        <div class="form-row">
          <button
            v-if="formStep > 0"
            @click="previousSection()"
            type="button"
            class="btn btn-black btn-sm col-md-2 col-4"
          >
            <span>Previous</span>
          </button>
          <div class="col-md-8 col-8 text-right ml-auto">
            <!-- <button
              type="button"
              @click="skipSection()"
              class="btn btn-success btn-sm mr-3 skipbutton"
              :disabled="$v.form.$invalid || $v.form2.$invalid || $v.form3.$invalid"
              v-if="formStep < formSteps.length - 2"
            >
              Skip
            </button> -->
            <button
              type="submit"
              class="btn btn-success btn-sm skipbutton"
              :disabled="$v.form.$invalid || $v.form2.$invalid || $v.form3.$invalid"
            >
              <span v-if="formStep < formSteps.length - 2 && !isUpdate">Next</span>
              <span v-if="formStep < formSteps.length - 2 && isUpdate">Saving...</span>
              <span v-if="formStep === formSteps.length - 2">Clone</span>
            </button>
          </div>
        </div>
        <div class="text-right text-sucess mt-3" v-if="isNextSaved">
          <span class="icofont-check text-success"></span>
          Successfully Saved !
        </div>
        <div class="text-right text-danger mt-3" v-if="isNextSaveFailed">
          <span class="icofont-warning text-danger"></span>
          Sorry, Saving failed. Refresh page and try again.
        </div>
        <div class="text-right text-danger mt-3" v-if="isDayError">
          <span class="icofont-warning text-danger"></span>
          You should add {{ form2.noOfDay }} itinerary(s).
        </div>
      </div>
    </form>

    <!-- success -->
    <div v-if="formStep === formSteps.length" class="my-4">
      <h1 class="text-success text-center mb-3"><strong>SUCCESS !</strong></h1>

      <div class="row justify-content-center">
        <div class="col-3 py-4 text-center">
          <span class="icofont-check icofont-10x text-success"></span>
        </div>
      </div>

      <div class="row justify-content-center">
        <div class="col-7 text-center">
          <h4 class="text-success text-center mt-4">You Have Successfully Cloned Tour</h4>
        </div>
      </div>
      <div class="row justify-content-center">
        <div class="col-7 text-center">
          <a class="mt-5 btn btn-success" href="/dashboard/tour">Go to Tours Page</a>
        </div>
      </div>
    </div>

    <!-- image select from library -->
    <app-tour-image-select></app-tour-image-select>
    <!-- image select from whole library -->
    <DestinationImageModal></DestinationImageModal>
  </div>
</template>

<style>
@import "vue-step-progress/dist/main.css";

span.badge {
  font-size: 13px;
}
span.badge a {
  cursor: pointer;
}
.step-progress__step-icon {
  font-size: 50px;
}
/* .skipbutton {
  min-width: 140px;
}
@media only screen and (max-width: 500px) {
  .skipbutton {
    min-width: 60px;
  }
} */
</style>

<script>
import { required, maxLength } from "vuelidate/lib/validators";
import $ from "jquery";
import StepProgress from "vue-step-progress";
import StarRating from "vue-star-rating";
import { mapGetters, mapActions } from "vuex";
import { Constants } from "@/constants";
import { randStrId } from "@/assets/js/custom-function";
import EastCountry from "@/assets/js/east-africa-country.json";
import ParkTicketFee from "@/assets/js/park-ticket-fee-rate.json";
import TourImageSelect from "@/components/TourImageSelect.vue";
import { VueEditor } from "vue2-editor";
import draggable from "vuedraggable";
import DestinationImageModal from "@/components/DestinationImageModal.vue";
import InputTagDropdownDestination from "@/components/InputTagDropdownDestination.vue";
import InputTagDropdownAirportInter from "@/components/InputTagDropdownAirportInter.vue";
import InputTagDropdownAirportLocal from "@/components/InputTagDropdownAirportLocal.vue";
import InputTagDropdownAccom from "@/components/InputTagDropdownAccom.vue";

export default {
  name: "TourClone",

  components: {
    "step-progress": StepProgress,
    "star-rating": StarRating,
    "app-tour-image-select": TourImageSelect,
    VueEditor,
    draggable,
    DestinationImageModal,
    InputTagDropdownDestination,
    InputTagDropdownAirportInter,
    InputTagDropdownAirportLocal,
    InputTagDropdownAccom,
  },

  data() {
    return {
      formSteps: ["Step 1", "Step 2", "Step 3", "Step 4", "Step 5", "Finish"],
      formStep: 0,
      form: {
        title: "",
        levels: [],
        countries: [],
        activities: [],
        transportation: [],
        noOfDay: 1,
        noOfNight: 0,
        tourGroup: "private",
        description: "",
        tourFocus: 23,
        gettingThere: {
          airportTransfer: "yes",
          bookFlight: "client-cost-yes",
          accomBeforeAfter: "extra-cost",
          internationalAirport: "Julius Nyerere International Airport",
          localInternationalAirport: "Kilimanjaro International Airport",
        },
      },
      form2: {
        customizable: true,
        parkRates: {
          parkFee: true,
        },
        dayByDay: [
          {
            description: "",
            destination: "",
            camping: false,
            accomType: "",
            campType: "",
            accomLevel: "",
            accomLocation: "",
            accomParticipation: false,
            accomPhotos: [],
          },
        ],
        rates: {
          discount: 0,
          cardPayment: "yes",
          adultCurrencyWinter: [0, 0, 0, 0, 0, 0],
          adultCurrencySpring: [0, 0, 0, 0, 0, 0],
          adultCurrencySummer: [0, 0, 0, 0, 0, 0],
          adultCurrencyAutumn: [0, 0, 0, 0, 0, 0],
          childCurrencyWinter: [0, 0, 0, 0, 0, 0],
          childCurrencySpring: [0, 0, 0, 0, 0, 0],
          childCurrencySummer: [0, 0, 0, 0, 0, 0],
          childCurrencyAutumn: [0, 0, 0, 0, 0, 0],
        },
      },
      form3: {
        image: "",
        banner: "",
        bannerType: "image",
        gallery: [],
      },
      form4: {
        inclusion: [],
        guideOverview: [],
        mealTypes: [],
        vaccines: [],
        disclaimers: {
          exactordersubjectavailability: { tourNote: 20, accept: "yes" },
          fixedflightbudget: { tourNote: 22, accept: "no" },
          fullalter: { tourNote: 21, accept: "yes" },
        },
        additionalInfo: {
          cancellation: "",
          freeDateChange: true,
          issueCreditNote: true,
          departure: true,
          arrivalVisa: true,
          advancePayment: "5%",
        },
        whyUs: null,
      },
      dayByDay: [{ day: "", title: "Day Title", id: randStrId() }],
      kilimanjaroInformationChecked: false,
      safariJeepChecked: false,
      // CKE_config: {
      //   height: 300,
      //   removeButtons: "About",
      //   extraPlugins: "justify",
      // },

      isUpdate: false,
      isDaySaved: false,
      isNextSaved: false,
      isDaySaveFailed: false,
      isNextSaveFailed: false,

      isDayError: false,

      isUploading: false,
      isDeleting: false,
      isUploadingFailed: false,

      update: 0,
      isNoAccom: new Array(50).fill(false),
      keyword: "",
      activityList: [],

      extraInclusion: [],
      extraExclusion: [],
    };
  },

  watch: {
    keyword: function () {
      if (this.keyword && this.tourActivities != undefined) {
        this.activityList = this.tourActivities.filter((item) => {
          return this.keyword
            .toLowerCase()
            .split(" ")
            .every((v) => item.name.toLowerCase().includes(v));
        });
      } else {
        this.activityList = this.tourActivities;
      }
    },
  },

  computed: {
    ...mapGetters([
      "user",
      "tourPackage",
      "tourNotes",
      "tourVaccines",
      "tourGuides",
      "tourMealTypes",
      "tourLevels",
      "tourFocus",
      "tourCities",
      "tourActivities",
      "tourOtherActivities",
      "tourKilimanjaroInformation",
      "tourTransports",
      "airportsTanzania",
      "airportsTanzaniaInternational",
      "accomLevels",
      "accomTypes",
      "accomLocations",
      "accomFacilities",
      "campTypes",
      "tourDestinations",
      "tourMeals",
      "Accommodation",
      "token"
    ]),

    tourPackageId() {
      return this.$route.params.id;
    },

    noteList() {
      return this.tourNotes;
    },

    guideOverview() {
      return this.tourGuides;
    },

    mealTypeList() {
      return this.tourMealTypes;
    },

    vaccineList() {
      return this.tourVaccines;
    },

    levelList() {
      return this.tourLevels;
    },

    mealList() {
      return this.tourMeals;
    },

    accomLevelList() {
      return this.accomLevels;
    },

    accomTypeList() {
      return this.accomTypes;
    },

    accomLocationList() {
      return this.accomLocations;
    },

    accomFacilityList() {
      return this.accomFacilities;
    },

    campTypeList() {
      return this.campTypes;
    },

    dayDestination() {
      return this.tourDestinations;
    },

    countryList() {
      return EastCountry;
    },

    parkTicketFeeList() {
      return ParkTicketFee;
    },

    focusList() {
      return this.tourFocus;
    },

    // activityList() {
    //   return this.tourActivities;
    // },

    otherActivityList() {
      return this.tourOtherActivities;
    },

    kilimanjaroInformationList() {
      return this.tourKilimanjaroInformation;
    },

    transportList() {
      return this.tourTransports;
    },

    airportTanzaniaList() {
      return this.airportsTanzania;
    },

    airportTanzaniaInternationalList() {
      return this.airportsTanzaniaInternational;
    },

    safariVehicle() {
      let vehicle = {};

      // safari jeep
      const safariJeep = this.tourTransports.find((t) => t.inputId === "safari-jeep");
      vehicle.safariJeep = safariJeep.facilities;

      return vehicle;
    },
  },

  mounted() {
    if (localStorage.getItem(Constants.TOUR_FORM_EDIT)) {
      // delete stored package if exist
      localStorage.removeItem(Constants.TOUR_FORM_EDIT);
      localStorage.removeItem(Constants.TOUR_FORM_EDIT_STEP);
    }

    this.$eventHub.$on("app-mounted", async () => {
      await Promise.all([
        this.fetchTourLevels(),
        this.fetchTourCities(),
        this.fetchTourFocus(),
        this.fetchTourActivities(),
        this.fetchTourOtherActivities(),
        this.fetchTourKilimanjaroInformation(),
        this.fetchTourTransports(),
        this.fetchAirportsTanzania(),
        this.fetchAirportsTanzaniaInternational(),
        this.fetchAccomLevels(),
        this.fetchAccomTypes(),
        this.fetchCampTypes(),
        this.fetchAccomLocations(),
        this.fetchTourDestinations(),
        this.fetchAccomFacilities(),
        this.fetchTourMeals(),
        this.fetchTourMealTypes(),
        this.fetchTourNotes(),
        this.fetchTourVaccines(),
        this.fetchTourGuides(),
        this.fetchAccommodationAll(),
        this.fetchTourPackage(this.tourPackageId),
      ]);

      this.cityAutocomplete();
      this.hideBarLoading();
      this.savedData();
      this.activityList = this.tourActivities;

      this.$eventHub.$on("day-gallery-photo", (image, day) => {
        if (image) {
          $("#tour-bannerday-preview-" + day).attr("src", image);
          this.form2.dayByDay[day].image = image;
        }
        $("#day-photo-" + day).val(image);
      });

      this.$eventHub.$on("tour-gallery-photo", (image) => {
        this.form3.image = image;
      });

      this.$eventHub.$on("banner-gallery-photo", (image) => {
        this.form3.banner = image;
      });

      this.$eventHub.$on("add-gallery-photo", (image) => {
        this.form3.gallery.push(...image);
      });
    });
  },

  validations() {
    if (this.formStep === 0) {
      // validate step 1 inputs
      return {
        form2: {},
        form3: {},
        form: {
          title: { required },
          description: { required },
          levels: { required, maxLength: maxLength(2) },
          countries: { required },
          activities: { required },
          transportation: { required },
          gettingThere: {
            startCity: { required },
            endCity: { required },
          },
        },
      };
    } else if (this.formStep === 1) {
      // validate step 2 inputs
      return {
        form: {},
        form3: {},
        form2: {
          dayByDay: {
            $each: {
              destination: { required },
              description: { required },
            },
          },
        },
      };
    } else if (this.formStep === 3) {
      // validate step 3 inputs
      return {
        form: {},
        form2: {},
        form3: {
          image: { required },
          banner: { required },
          gallery: { required, maxLength: maxLength(12) },
        },
      };
    } else {
      return { form: {}, form2: {}, form3: {}, form4: {} };
    }
  },

  methods: {
    ...mapActions([
      "createPackage",
      "uploadTourPhoto",
      "uploadTourInnerPhoto",
      "uploadTourGalleryPhoto",
      "uploadTourDayPhoto",
      "deleteTourPhoto",
      "uploadAccommodationPhotoPackage",
      "deleteAccommodationPhoto",
      "fetchTourPackage",
      "fetchTourNotes",
      "fetchTourVaccines",
      "fetchTourGuides",
      "fetchTourMealTypes",
      "fetchTourLevels",
      "fetchTourCities",
      "fetchTourFocus",
      "fetchTourActivities",
      "fetchTourOtherActivities",
      "hideBarLoading",
      "fetchTourKilimanjaroInformation",
      "fetchTourTransports",
      "fetchAirportsTanzania",
      "fetchAirportsTanzaniaInternational",
      "fetchAccomLevels",
      "fetchAccomTypes",
      "fetchCampTypes",
      "fetchAccomLocations",
      "fetchTourDestinations",
      "fetchAccomFacilities",
      "fetchTourMeals",
      "showSpinnerLoading",
      "hideSpinnerLoading",
      "fetchAccommodationAll"
    ]),

    async savedData() {
      if (!localStorage.getItem(Constants.TOUR_FORM_EDIT)) {
        const packageData = this.tourPackage;
        packageData["user"] = packageData.operator.id;

        for (let index = 0; index < packageData.dayByDay.length; index++) {
          if (packageData.dayByDay[index].accomName == "" && packageData.dayByDay[index].campName == "")
            this.isNoAccom[index] = true;
        }

        delete packageData.id;
        delete packageData.operator;
        delete packageData.createdAt;
        delete packageData.updatedAt;
        delete packageData.isDelete;
        delete packageData.position;
        delete packageData.published;
        delete packageData.status;

        await localStorage.setItem(Constants.TOUR_FORM_EDIT, JSON.stringify(packageData));
        await localStorage.setItem(Constants.TOUR_FORM_EDIT_STEP, JSON.stringify(0));
      }

      let storeData = {};

      if (localStorage.getItem(Constants.TOUR_FORM_EDIT)) {
        storeData = JSON.parse(localStorage.getItem(Constants.TOUR_FORM_EDIT));
        this.formStep = parseInt(localStorage.getItem(Constants.TOUR_FORM_EDIT_STEP));

        // step 1 form
        if (this.formStep === 0) {
          this.form["transportation"] = [];

          // transportation field
          storeData.tourTransports.forEach((item) => {
            this.form.transportation.push(item.transport);

            // safari jeep facilities
            if (item.transport == 2) {
              this.safariJeepChecked = true;
              this.form.safariJeepFacility = item.facilities;
            }
          });
          delete storeData.tourTransports;

          // kilimanjaro information selection
          if (storeData.kilimanjaroInformation.length) {
            const incl = [2, 4, 6, 7];

            if (storeData.kilimanjaroInformation.some((v) => incl.includes(v))) {
              this.kilimanjaroInformationChecked = true;
            }
          }

          $.extend(this.form, storeData);
        }

        // step 2 form
        if (this.formStep === 1 || this.formStep === 2) {
          if (storeData.rates) {
            storeData.rates["adultCurrencyWinter"] = [];
            storeData.rates["adultCurrencySummer"] = [];
            storeData.rates["adultCurrencySpring"] = [];
            storeData.rates["adultCurrencyAutumn"] = [];
            storeData.rates["childCurrencyWinter"] = [];
            storeData.rates["childCurrencySummer"] = [];
            storeData.rates["childCurrencySpring"] = [];
            storeData.rates["childCurrencyAutumn"] = [];

            storeData.rates.adult.forEach((rate) => {
              storeData.rates.adultCurrencyWinter.push(rate.currencyWinter);
              storeData.rates.adultCurrencySummer.push(rate.currencySummer);
              storeData.rates.adultCurrencySpring.push(rate.currencySpring);
              storeData.rates.adultCurrencyAutumn.push(rate.currencyAutumn);
            });

            storeData.rates.child.forEach((rate) => {
              storeData.rates.childCurrencyWinter.push(rate.currencyWinter);
              storeData.rates.childCurrencySummer.push(rate.currencySummer);
              storeData.rates.childCurrencySpring.push(rate.currencySpring);
              storeData.rates.childCurrencyAutumn.push(rate.currencyAutumn);
            });
            delete storeData.rates.adult;
            delete storeData.rates.child;
          }

          // set day by day details
          if (storeData.dayByDay) {
            if (storeData.dayByDay.length > 0) {
              this.dayByDay = [];

              storeData.dayByDay.forEach((day) => {
                this.dayByDay.push({ day: day.day, title: day.title, id: randStrId() });
              });

              storeData.dayByDay.forEach((day, index) => {
                // destination image
                const destination = day.destination.split(" ").join("_");
                this.$eventHub.$emit("gallery-photo-destination", destination, index, 1);
              });
            }  else {
              this.dayByDay.push({ day: "", title: "Day Title", id: randStrId() });
            }
          }

          if(storeData.dayByDay.length == 0 ) {
            storeData.dayByDay.push({ day: '', title: '', id: randStrId() });
          }

          $.extend(this.form2, storeData);
        }

        // step 3 form
        if (this.formStep === 3) {
          // set gallery photos
          if (storeData.dayByDay) {
            if (storeData.dayByDay.length > 0) {
              storeData.dayByDay.forEach((day, index) => {
                const destination = day.destination.split(" ").join("_");
                this.$eventHub.$emit("gallery-photos", destination);
              });
            }
          }

          $.extend(this.form3, storeData);
        }

        // step 4 form
        if (this.formStep === 4) {
          //disclaimer fix - 2022-11-24 - Vlad
          delete storeData.disclaimers.exactordersubjectavailability._id;
          delete storeData.disclaimers.fixedflightbudget._id;
          delete storeData.disclaimers.fullalter._id;

          //Inclusion/exclusion fix - 2022-11-24 - Vlad
          let inclusion = [];
          let flag = 0,
            count = 0;
          for (let i = 0; i < this.noteList.length; i++) {
            flag = 0;
            for (let j = 0; j < storeData.inclusion.length; j++) {
              if (this.noteList[i].id == storeData.inclusion[j].tourNote) {
                inclusion.push({ name: "", description: "", tourNote: 0, included: false, excluded: false });
                inclusion[count].name = this.noteList[i].name;
                inclusion[count].description = this.noteList[i].description;
                inclusion[count].tourNote = this.noteList[i].id;
                inclusion[count].included = storeData.inclusion[j].included;
                inclusion[count].excluded = !storeData.inclusion[j].included;
                flag = 1; // inserted
                count++;
                break;
              }
            }
            if (flag == 0 && this.noteList[i].name != "Disclaimer") {
              inclusion.push({ name: "", description: "", tourNote: 0, included: false, excluded: false });
              inclusion[count].name = this.noteList[i].name;
              inclusion[count].description = this.noteList[i].description;
              inclusion[count].tourNote = this.noteList[i].id;
              inclusion[count].included = null;
              inclusion[count].excluded = null;
              count++;
            }
          }

          storeData.inclusion = inclusion;

          $.extend(this.form4, storeData);
        }
      }
    },

    async tourClone() {
      this.isDayError = false;
      this.goTop();
      let submitData = {};

      // step 1 form
      if (this.formStep === 0) {
        submitData = this.form;
        submitData["tourTransports"] = [];

        // tour transport field
        submitData.transportation.forEach((transport) => {
          let facilities = [];

          // safari jeep facilities
          if (transport === 2) {
            facilities = submitData.safariJeepFacility;
          }

          submitData.tourTransports.push({
            transport,
            facilities,
          });
        });

        // remove unnecessary fields
        delete submitData.transportation;
        delete submitData.safariJeepFacility;
      }

      // step 2 form
      if (this.formStep === 1 || this.formStep === 2) {
        //have to check Day length and No of day
        if (this.form2.dayByDay.length > this.form2.noOfDay) {
          //not correct of day length and no of day
          this.isDayError = true;
          this.isUpdate = false;

          return;
        }
        const temp = this.form2;
        await localStorage.setItem("TEMP_VLAD_STEPTWO_DATA", JSON.stringify(temp));
        submitData = JSON.parse(localStorage.getItem("TEMP_VLAD_STEPTWO_DATA"));
        submitData.rates["adult"] = [];
        submitData.rates["child"] = [];

        // set rates
        submitData.rates.adultCurrencyWinter.forEach((_rate, index) => {
          submitData.rates.adult.push({
            currencyWinter: JSON.parse(submitData.rates.adultCurrencyWinter[index]),
            currencySummer: JSON.parse(submitData.rates.adultCurrencySummer[index]),
            currencySpring: JSON.parse(submitData.rates.adultCurrencySpring[index]),
            currencyAutumn: JSON.parse(submitData.rates.adultCurrencyAutumn[index]),
            noOfPerson: index + 1,
          });

          submitData.rates.child.push({
            currencyWinter: JSON.parse(submitData.rates.childCurrencyWinter[index]),
            currencySummer: JSON.parse(submitData.rates.childCurrencySummer[index]),
            currencySpring: JSON.parse(submitData.rates.childCurrencySpring[index]),
            currencyAutumn: JSON.parse(submitData.rates.childCurrencyAutumn[index]),
            noOfPerson: index + 1,
          });
        });

        submitData.rates.discount = JSON.parse(submitData.rates.discount);

        // set park rates
        if (submitData.parkRates.parkFee) {
          this.parkTicketFeeList.forEach((fee) => {
            if (submitData.parkRates[fee.name]) {
              submitData.parkRates[fee.name] = JSON.parse(submitData.parkRates[fee.name]);
            }
          });
        } else {
          // replace parkfee detail if false
          submitData.parkRates = { parkFee: false };
        }

        // remove unnecessary fields
        delete submitData.rates.adultCurrencyWinter;
        delete submitData.rates.adultCurrencySummer;
        delete submitData.rates.adultCurrencySpring;
        delete submitData.rates.adultCurrencyAutumn;
        delete submitData.rates.childCurrencyWinter;
        delete submitData.rates.childCurrencySummer;
        delete submitData.rates.childCurrencySpring;
        delete submitData.rates.childCurrencyAutumn;

        // convert fields
        submitData.dayByDay.forEach((day) => {
          day.camping = JSON.parse(day.camping);
          day.accomParticipation = (day.accomParticipation == undefined || day.accomParticipation == "") ? "" : JSON.parse(day.accomParticipation);
        });
        submitData.customizable = JSON.parse(submitData.customizable);
        submitData.parkRates.parkFee = JSON.parse(submitData.parkRates.parkFee);
      }

      // step 3 form
      if (this.formStep === 3) {
        submitData = this.form3;
      }

      // step 4 form
      if (this.formStep === 4) {
        submitData = this.form4;

        // set additional information
        submitData.additionalInfo.freeDateChange = JSON.parse(submitData.additionalInfo.freeDateChange);
        submitData.additionalInfo.issueCreditNote = JSON.parse(submitData.additionalInfo.issueCreditNote);
        submitData.additionalInfo.departure = JSON.parse(submitData.additionalInfo.departure);
        submitData.additionalInfo.arrivalVisa = JSON.parse(submitData.additionalInfo.arrivalVisa);
      }

      // save data to local storage
      let storeData = {};

      if (localStorage.getItem(Constants.TOUR_FORM_EDIT)) {
        storeData = JSON.parse(localStorage.getItem(Constants.TOUR_FORM_EDIT));
      }

      $.extend(storeData, submitData);

      localStorage.setItem(Constants.TOUR_FORM_EDIT, JSON.stringify(storeData));

      if (this.formStep === this.formSteps.length - 2) {
        // prepare the submit data for inclusion changed - 2022-11-24
        let inclusion = [];
        let count = 0;
        for (let i = 0; i < submitData.inclusion.length; i++) {
          if (submitData.inclusion[i].included == true) {
            inclusion.push({ tourNote: 0, included: false });
            inclusion[count].tourNote = submitData.inclusion[i].tourNote;
            inclusion[count].included = true;
            count++;
          } else if (submitData.inclusion[i].excluded == true) {
            inclusion.push({ tourNote: 0, included: false });
            inclusion[count].tourNote = submitData.inclusion[i].tourNote;
            inclusion[count].included = false;
            count++;
          }
        }

        submitData.inclusion = inclusion;

        // save data to database
        this.showSpinnerLoading();

        await this.createPackage(submitData).then((_res) => {
          this.hideSpinnerLoading();
          this.formStep = this.formSteps.length;
          localStorage.removeItem(Constants.TOUR_FORM_EDIT_STEP);
          localStorage.removeItem(Constants.TOUR_FORM_EDIT);
          localStorage.removeItem("TEMP_VLAD_STEPTWO_DATA");
        });
      } else {
        this.formStep = this.formStep + 1;
        localStorage.setItem(Constants.TOUR_FORM_EDIT_STEP, this.formStep.toString());
        this.savedData();
      }
    },

    async previousSection() {
      this.goTop();
      this.formStep -= 1;
      localStorage.setItem(Constants.TOUR_FORM_EDIT_STEP, this.formStep.toString());
      this.savedData();
    },

    otherActivityInput(e) {
      if (e.target.checked) {
        $("#other-activity input").prop("checked", false);
        $(e.target).prop("checked", true);
      }
    },

    kilimanjaroInfoChecked(e) {
      const incl = ["2", "4", "6", "7"];
      if (incl.includes(e.target.value) && e.target.checked == true) {
        // kilimanjaro information selection
        this.kilimanjaroInformationChecked = true;
        this.form.kilimanjaroInformation = [];
      } else {
        this.kilimanjaroInformationChecked = false;
        this.form.kilimanjaroInformation = [];
      }
    },

    transportationChecked(e) {
      if (e.target.value == 2) {
        // safari jeep facility selection
        if (e.target.checked) {
          this.safariJeepChecked = true;
          this.form.safariJeepFacility = [];
        } else {
          this.safariJeepChecked = false;
          this.form.safariJeepFacility = [];
        }
      }
    },

    cityAutocomplete() {
      let cities = [];

      this.tourCities.forEach((city) => {
        cities.push(city.name);
      });

      $("[data-autocomplete=city]").autocomplete({
        source: cities,
      });
    },

    navigateDay(day) {
      setTimeout(() => {
        $("html, body").animate(
          {
            scrollTop: $("#day-btn-" + day).offset().top - 40 + "px",
          },
          500
        );
      }, 1000);
    },

    addDayByDay() {
      this.dayByDay.push({
        day: "",
        title: "Day Title",
        id: randStrId(),
      });
      this.form2.dayByDay.push({
        description: "",
        destination: "",
        camping: false,
        accomType: "",
        campType: "",
        accomLevel: "",
        accomLocation: "",
        accomParticipation: false,
        accomPhotos: [],
      });

      this.$eventHub.$emit("gallery-day-destination", null);
    },

    removeDayByDay(index) {
      this.dayByDay.splice(index, 1);
      this.form2.dayByDay.splice(index, 1);
      this.$eventHub.$emit("gallery-day-destination", index);
    },

    noAccommodation(day) {
      if (this.isNoAccom[day]) {
        this.form2.dayByDay[parseInt(day)].accomLevel = "";
        this.form2.dayByDay[parseInt(day)].accomLocation = "";
        this.form2.dayByDay[parseInt(day)].accomName = "";
        this.form2.dayByDay[parseInt(day)].campName = "";
        this.form2.dayByDay[parseInt(day)].accomParticipation = false;
        this.form2.dayByDay[parseInt(day)].accomType = "";
        this.form2.dayByDay[parseInt(day)].accomWebsite = "";
        this.form2.dayByDay[parseInt(day)].accomType = "";
      }
    },

    imageSelect(event, selector) {
      event.preventDefault();

      let daySelector = event.target.href;
      daySelector = daySelector.substring(daySelector.indexOf("#") + 1);

      this.$eventHub.$emit("gallery", selector, daySelector);

      $("#image-select-container").modal("show");
    },

    imageSelectNew(event, selector) {
      event.preventDefault();

      let daySelector = event.target.href;
      daySelector = daySelector.substring(daySelector.indexOf("#") + 1);

      this.$eventHub.$emit("gallery", selector, daySelector);

      $("#destination-image-modal").modal("show");
    },

    camping(index) {
      $('[data-camping="no-' + index + '"]').toggle();
      $('[data-camping="yes-' + index + '"]').toggle();
      $('[data-accom-rating="accom-' + index + '"]').toggle();
    },

    websiteNo(day) {
      $("#day-accom-website-" + day).toggle();
      $("#website-test-" + day).prop("href", "#");

      this.form2.dayByDay[parseInt(day)].accomWebsite = null;
    },

    websiteTest(e, day) {
      var str = e.target.value;
      var id = e.target.id;

      if (str.match(/http:\/\//g) || str.match(/https:\/\//g)) {
        $("#" + id).val(str);
        $("#website-test-" + day).prop("href", str);
      } else {
        $("#" + id).val("http://" + str);
        $("#website-test-" + day).prop("href", "http://" + str);
      }
    },

    bannerFilter(day) {
      // let destination = $("#day-destination-" + day).val();
      let destination = this.form2.dayByDay[day].destination;

      $("#destination-text-" + day).hide();

      if (destination) {
        $("#banner-select-" + day).hide();

        destination = destination.split(" ").join("_");
        this.$eventHub.$emit("gallery-photo-destination", destination, day, 1);
      } else {
        $("#banner-photo-" + day).hide();
        $("#banner-select-" + day).show();
      }
    },

    tourBannerType(type) {
      this.form3.banner = "";

      if (type == "video") {
        setTimeout(() => {
          this.form3.banner = process.env.VUE_APP_TOUR_BANNER_YOUTUBE_URL;
        }, 1200);
      }
    },

    tourBannerVideo(e) {
      let url = e.target.value;
      if (url) {
        this.form3.banner = url;
      } else {
        this.form3.banner = "https://www.youtube.com/watch?v=dY7IJ5Az4j";
      }
    },

    galleryPhotoRemove(index) {
      this.form3.gallery.splice(index, 1);
    },

    deleteAccomImage(index, day) {
      const dP = this;
      const data = {
        photoUrl: this.form2.dayByDay[day].accomPhotos[index],
      };
      this.isDeleting = true;
      $.confirm({
        title: "",
        content: "Are you sure to delete this accommodation image ?",
        buttons: {
          confirm: async () => {
            await dP.deleteAccommodationPhoto(data).then(() => {
              dP.form2.dayByDay[day].accomPhotos.splice(index, 1);
              this.isDeleting = false;
            });
          },
          cancel: () => {
            this.isDeleting = false;
          },
        },
      });
    },

    handleAccomImage(e, day) {
      if (this.form2.dayByDay[day].accomName == "" || this.form2.dayByDay[day].accomName == undefined) {
        $.alert("Please input the accommodation name first.");
        return;
      }
      var currentdate = new Date();
      var datetime =
        "_" +
        currentdate.getFullYear() +
        "_" +
        (currentdate.getMonth() + 1) +
        "_" +
        currentdate.getDate() +
        "_" +
        currentdate.getHours() +
        "_" +
        currentdate.getMinutes() +
        "_" +
        currentdate.getSeconds();
      let filename = this.form2.dayByDay[day].accomName.toUpperCase().replace(/ /g, "_");
      filename = filename + datetime;
      const selectedImage = e.target.files[0]; // get first file
      let formData = new FormData();
      formData.append("photo", selectedImage);
      formData.append("newName", filename);
      this.isUploadingFailed = false;
      this.isUploading = true;
      this.uploadAccommodationPhotoPackage(formData)
        .then((_res) => {
          if (this.form2.dayByDay[day].accomPhotos == undefined) {
            this.form2.dayByDay[day]["accomPhotos"] = [];
          }
          this.form2.dayByDay[day].accomPhotos.push(_res.data.filepath);
          this.form2.dayByDay[day].accomPhotos.slice();
          this.isUploading = false;
        })
        .catch((e) => {
          console.log(e);
          this.isUploadingFailed = true;
          this.isUploading = false;
        });
    },

    handleTourImage(e) {
      var currentdate = new Date();
      var datetime =
        "_" +
        currentdate.getFullYear() +
        "_" +
        (currentdate.getMonth() + 1) +
        "_" +
        currentdate.getDate() +
        "_" +
        currentdate.getHours() +
        "_" +
        currentdate.getMinutes() +
        "_" +
        currentdate.getSeconds();
      let filename = "CUSTOM_TOUR";
      filename = filename + datetime;
      const selectedImage = e.target.files[0]; // get first file
      let formData = new FormData();
      formData.append("photo", selectedImage);
      formData.append("newName", filename);
      this.isUploading = true;
      this.isUploadingFailed = false;
      this.uploadTourPhoto(formData)
        .then((_res) => {
          this.form3.image = _res.data.filepath;
          this.isUploading = false;
        })
        .catch((e) => {
          console.log(e);
          this.isUploadingFailed = true;
          this.isUploading = false;
        });
    },

    handleTourDayImage(e, day) {

      var currentdate = new Date();
      var datetime =
        "_" +
        currentdate.getFullYear() +
        "_" +
        (currentdate.getMonth() + 1) +
        "_" +
        currentdate.getDate() +
        "_" +
        currentdate.getHours() +
        "_" +
        currentdate.getMinutes() +
        "_" +
        currentdate.getSeconds();
      let filename = "CUSTOM_TOUR_DAY";
      filename = filename + datetime;
      const selectedImage = e.target.files[0]; // get first file
      let formData = new FormData();
      formData.append("photo", selectedImage);
      formData.append("newName", filename);
      this.isUploading = true;
      this.isUploadingFailed = false;
      this.uploadTourDayPhoto(formData)
        .then((_res) => {
          this.form2.dayByDay[day].image = _res.data.filepath;
          this.isUploading = false;
        })
        .catch((e) => {
          console.log(e);
          this.isUploadingFailed = true;
          this.isUploading = false;
        });
    },

    handleTourInnerImage(e) {
      var currentdate = new Date();
      var datetime =
        "_" +
        currentdate.getFullYear() +
        "_" +
        (currentdate.getMonth() + 1) +
        "_" +
        currentdate.getDate() +
        "_" +
        currentdate.getHours() +
        "_" +
        currentdate.getMinutes() +
        "_" +
        currentdate.getSeconds();
      let filename = "CUSTOM_TOUR_INNER";
      filename = filename + datetime;
      const selectedImage = e.target.files[0]; // get first file
      let formData = new FormData();
      formData.append("photo", selectedImage);
      formData.append("newName", filename);
      this.isUploading = true;
      this.isUploadingFailed = false;
      this.uploadTourInnerPhoto(formData)
        .then((_res) => {
          this.form3.banner = _res.data.filepath;
          this.isUploading = false;
        })
        .catch((e) => {
          console.log(e);
          this.isUploadingFailed = true;
          this.isUploading = false;
        });
    },

    handleTourGalleryImage(e) {
      var currentdate = new Date();
      var datetime =
        "_" +
        currentdate.getFullYear() +
        "_" +
        (currentdate.getMonth() + 1) +
        "_" +
        currentdate.getDate() +
        "_" +
        currentdate.getHours() +
        "_" +
        currentdate.getMinutes() +
        "_" +
        currentdate.getSeconds();
      let filename = "CUSTOM_TOUR_GALLERY";
      filename = filename + datetime;
      const selectedImage = e.target.files[0]; // get first file
      let formData = new FormData();
      formData.append("photo", selectedImage);
      formData.append("newName", filename);
      this.isUploading = true;
      this.isUploadingFailed = false;
      this.uploadTourGalleryPhoto(formData)
        .then((_res) => {
          this.form3.gallery.push(_res.data.filepath);
          this.isUploading = false;
        })
        .catch((e) => {
          console.log(e);
          this.isUploadingFailed = true;
          this.isUploading = false;
        });
    },

    goTop() {
      setTimeout(() => {
        $("html, body").animate(
          {
            scrollTop: $("#page-container").offset().top - 40 + "px",
          },
          500
        );
      }, 1000);
    },

    accomSelect(index) {
      const filteredAccomItem = this.Accommodation.filter((item) => {
        return item.accomName == this.form2.dayByDay[index].accomName;
      });

      this.form2.dayByDay[index].accomRating = filteredAccomItem[0].rate;
      this.form2.dayByDay[index].accomLevel = filteredAccomItem[0].accomLevel;
      this.form2.dayByDay[index].accomType = filteredAccomItem[0].accomType;
      this.form2.dayByDay[index].accomWebsite = filteredAccomItem[0].accomWebsite;
      this.form2.dayByDay[index].accomPhotos = filteredAccomItem[0].photos;

    },

    removeAcitivity(id) {
      this.form.activities = this.form.activities.filter((number) => number !== id);
    },

    addExtraInclusion() {
      this.extraInclusion.push("");
    },
    addExtraExclusion() {
      this.extraExclusion.push("");
    },

    removeExtraInclusion(index) {
      this.extraInclusion.splice(index, 1);
    },
    removeExtraExclusion(index) {
      this.extraExclusion.splice(index, 1);
    },
    previewPackage() {
      window.open(process.env.VUE_APP_PREVIEW_PACKAGE_URL + this.tourPackageId + "?preivewId=" + this.token, "_blank");
    },
  },
};
</script>
